'use client';
import { Suspense } from 'react';
import Link from 'next/link';
import { useParams, useSearchParams, useRouter } from 'next/navigation';
import { useState, useEffect, useRef, memo, useMemo, useCallback, Fragment } from 'react';
import { Stack, Paper, Container, Image, Title, Accordion, Grid, Text, Button, Box, Breadcrumbs, Group, Badge, Loader, Center, Alert, ActionIcon, Select, Modal, FileInput, NumberInput, Checkbox, Tooltip, Slider, TextInput, Divider, Skeleton, Popover, SegmentedControl, Menu } from '@mantine/core';
import { useDebouncedCallback, useMediaQuery } from '@mantine/hooks';
import { IconFileTypePdf, IconFileSpreadsheet, IconMoneybag, IconHash, IconCube, IconAlertTriangle, IconDownload, IconEdit, IconCheck, IconUnlink, IconTrash, IconUpload, IconStack2, IconInfoCircle, IconChecklist, IconRefresh, IconChevronDown, IconSquareLetterR, IconSquareLetterB, IconPhoto, Icon3dCubeSphere, IconRotate, IconEye, IconArrowRight, IconArrowLeft, IconPlus, IconUsers, IconSearch, IconX, IconArrowsExchange, IconShoppingCart } from '@tabler/icons-react';
import { Collapse } from '@mantine/core';
import dynamic from 'next/dynamic';
import { LegoBag } from '@/components/LegoBag';
import { AdSlot } from '@/components/AdSlot';

// three.js is only needed when the user opens the 3D viewer - keep it out of the page bundle
const LDrawViewer = dynamic(
  () => import('@/components/LDrawViewer').then((m) => m.LDrawViewer),
  { ssr: false, loading: () => <Center h={450}><Loader /></Center> }
);

import { SetLabels } from '@/components/SetLabels';
import { VerificationBadge } from '@/components/VerificationBadge';
import { trackEvent } from '@/lib/telemetry';
import { useImageBackgroundColor } from '@/hooks/useImageBackgroundColor';
import { useAuth } from '@/context/AuthContext';
import { useUser } from '@/context/UserContext';
import { FavoriteHeart } from '@/components/FavoriteHeart';
import { useSortProgress } from '@/hooks/useSortProgress';
import api from '@/lib/api';
import { notifyError } from '@/lib/notify';
import { ERROR_KINDS, friendlyMessage } from '@/lib/apiError';
import { getImageUrl, getThumbUrl } from '@/lib/imageUrl';
import { tokenizePartQuery, buildPartHaystack, partMatchesTokens } from '@/lib/partSearch';
import { sortColorOptions } from '@/lib/legoColors';
import { groupPartsByType } from '@/lib/partTypes';
import { usePartOrder } from '@/hooks/usePartOrder';
import { downloadBagBsx, downloadSetBsx } from '@/lib/bsx';
import { WhatsNext } from '@/components/WhatsNext';
import { isSortModeNew } from '@/lib/sortModeLaunch';
import bricklinkDesignerProgram from '@/data/bricklink-designer-program';
import { legoProductUrl, affiliateRel, affiliateEnabled, isEuVisitor } from '@/lib/affiliate';


// Check if a part is decorated/printed (pr, pb, pat, p followed by digits)
function isDecoratedPart(partExId) {
  if (!partExId) return false;
  return /(?:pr|pb|pat|p)\d+/.test(partExId);
}

function isColorLight(hexColor) {
  if (!hexColor) return true;
  const color = hexColor.substring(1);
  const r = parseInt(color.substring(0, 2), 16);
  const g = parseInt(color.substring(2, 4), 16);
  const b = parseInt(color.substring(4, 6), 16);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 150;
}

function activateOnKeyboard(event, action) {
  if (event.key !== 'Enter' && event.key !== ' ') return;
  event.preventDefault();
  event.stopPropagation();
  action();
}

function getColorBrightness(hexColor) {
  if (!hexColor || hexColor === 'null' || hexColor === 'undefined') {
    return 999;
  }
  try {
    const color = hexColor.replace('#', '');
    const r = parseInt(color.substring(0, 2), 16);
    const g = parseInt(color.substring(2, 4), 16);
    const b = parseInt(color.substring(4, 6), 16);
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness;
  } catch (error) {
    console.warn('Error parsing color:', hexColor, error);
    return 999;
  }
}

function getImageSize(imageUrl) {
  const match = imageUrl?.match(/\/(\d+)x(\d+)\//);
  if (match) {
    return parseInt(match[1]) * parseInt(match[2]);
  }
  return 0;
}

function getInitialOpenBag(bags) {
  const firstBag = (bags || []).find((bag) => {
    if (bag.bagNumber === '0') return false;
    if (bag.bagNumber === 'Extra parts' && (!bag.parts || bag.parts.length === 0)) return false;
    return true;
  });
  return firstBag ? [`bag-${firstBag.bagNumber}`] : [];
}

function sortPartsByBrightness(parts, ascending = false) {
  if (!parts || parts.length === 0) return parts;
  return [...parts].sort((a, b) => {
    const brightnessA = getColorBrightness(a.colorHex);
    const brightnessB = getColorBrightness(b.colorHex);
    return ascending
      ? brightnessB - brightnessA
      : brightnessA - brightnessB;
  });
}

function MissingPartImage({ size = 96 }) {
  const compact = size <= 100;
  return (
    <Box
      role="img"
      aria-label="Part image unavailable"
      style={{
        width: size,
        height: size,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
        border: '1px solid #dee2e6',
        borderRadius: compact ? 7 : 14,
        backgroundColor: '#f8f9fa',
        backgroundImage: [
          'linear-gradient(to bottom right, transparent calc(50% - 0.5px), #e9ecef 50%, transparent calc(50% + 0.5px))',
          'linear-gradient(to top right, transparent calc(50% - 0.5px), #e9ecef 50%, transparent calc(50% + 0.5px))',
        ].join(', '),
      }}
    >
      <img
        src="/assets/part-image-unavailable.png"
        alt=""
        width={compact ? 78 : Math.round(size * 0.78)}
        height={compact ? 78 : Math.round(size * 0.78)}
        style={{ position: 'absolute', objectFit: 'contain', opacity: 0.12 }}
      />
      <Text
        component="span"
        size={compact ? "11px" : "lg"}
        c="#495057"
        fw={700}
        lh={1.15}
        ta="center"
        style={{ position: 'relative', textShadow: '0 1px 0 #fff' }}
      >
        No image<br />available
      </Text>
    </Box>
  );
}

const PartGridItem = memo(function PartGridItem({ part, bgColor, adminMode, onDisconnect, onDelete, onEdit, onQuantityEdit, onMove, extraParts, svgStatus, multiItemCount, onImageError, onImageLoad, partLocations, onJumpToBag, isHighlighted = false }) {
  const [imageVersion, setImageVersion] = useState(0);
  // 192px WebP-thumbnail (2x retina voor de 96px weergave) - veel kleiner dan het originele PNG
  const baseImageUrl = getThumbUrl(part.imageUrl, 192);
  const imageUrl = imageVersion > 0 ? `${baseImageUrl}?v=${imageVersion}` : baseImageUrl;
  const [imageLoadFailed, setImageLoadFailed] = useState(false);
  const imgRef = useRef(null);
  const reportedInitialLoad = useRef(false);

  const handleImageError = () => {
    reportedInitialLoad.current = true;
    setImageLoadFailed(true);
    if (onImageError) {
      const partKey = `${part.bagId}_${part.partId}_${part.color_id || 'nocolor'}`;
      onImageError(partKey);
    }
    if (onImageLoad) {
      onImageLoad();
    }
  };

  const handleImageLoad = () => {
    reportedInitialLoad.current = true;
    if (onImageLoad) {
      onImageLoad();
    }
  };

  // SSR'd images can finish loading before hydration attaches onLoad; without
  // this check the load is never reported and the staggered bag loader stalls
  useEffect(() => {
    const img = imgRef.current;
    if (img?.complete && !reportedInitialLoad.current) {
      if (img.naturalWidth > 0) handleImageLoad();
      else handleImageError();
    }
  }, []);

  useEffect(() => {
    setImageLoadFailed(false);
    reportedInitialLoad.current = false;
  }, [imageUrl]);

  const partKey = `${part.bagId}_${part.partId}_${part.color_id || 'nocolor'}`;

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [selectedPartId, setSelectedPartId] = useState(part.partId);
  const [selectedQuantity, setSelectedQuantity] = useState(part.quantity);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadScale, setUploadScale] = useState('100');
  const [partConfirmAction, setPartConfirmAction] = useState(null);
  const containerRef = useRef(null);

  const handleEditClick = () => {
    setEditModalOpen(true);
  };

  const handleEditSave = () => {
    if (selectedPartId && selectedPartId !== part.partId) {
      onEdit(part.bagId, part.partId, part.color_id, selectedPartId, selectedQuantity);
    } else if (selectedQuantity !== part.quantity) {
      onQuantityEdit(part.bagId, part.partId, part.color_id, selectedQuantity);
    }
    setEditModalOpen(false);
  };

  const handleUploadClick = () => {
    setUploadModalOpen(true);
  };

  const handleUploadSave = async () => {
    if (uploadFile) {
      try {
        await api.uploadPartImage(part.setId, part.bagId, part.imageUrl, uploadFile, parseInt(uploadScale));
        setUploadModalOpen(false);
        setUploadFile(null);
        setUploadScale('100');
        setImageVersion(v => v + 1);
      } catch (error) {
        console.error('Error uploading image:', error);
        notifyError(error, { title: 'Failed to upload image' });
      }
    }
  };

  const handleDisconnectClick = () => {
    setPartConfirmAction('disconnect');
  };

  const handleDeleteClick = () => {
    setPartConfirmAction('delete');
  };

  // Part-detail sheet: klik op een tegel (buiten check mode) opent de details -
  // 3D-viewer of grote afbeelding, kleur, ID's, vindplaatsen en externe links
  const [detailOpen, setDetailOpen] = useState(false);
  const viewer3dRef = useRef(null);

  const [rebrickableModalOpen, setRebrickableModalOpen] = useState(false);
  const [rebrickablePreview, setRebrickablePreview] = useState(null);
  const [rebrickableLoading, setRebrickableLoading] = useState(false);
  const [rebrickableError, setRebrickableError] = useState(null);
  const [rebrickableScale, setRebrickableScale] = useState(20);
  const [rebrickableUploadFile, setRebrickableUploadFile] = useState(null);
  const [rebrickableUploadScale, setRebrickableUploadScale] = useState('100');
  const [exportImages, setExportImages] = useState([]);
  const [exportImagesLoading, setExportImagesLoading] = useState(false);
  const [selectedExportImage, setSelectedExportImage] = useState(null);
  const [useLeftHalf, setUseLeftHalf] = useState(false);

  const handleRebrickableClick = async () => {
    setRebrickableModalOpen(true);
    setRebrickableLoading(true);
    setUseLeftHalf(false);
    setRebrickableError(null);
    setRebrickablePreview(null);
    setRebrickableScale(20);
    setRebrickableUploadFile(null);
    setRebrickableUploadScale('100');
    setExportImages([]);
    setExportImagesLoading(true);
    setSelectedExportImage(null);

    try {
      const [rebrickableResult, exportResult] = await Promise.allSettled([
        api.previewRebrickableImage(part.partId, 20),
        api.getExportImages(part.partId)
      ]);

      if (rebrickableResult.status === 'fulfilled') {
        setRebrickablePreview(rebrickableResult.value);
      } else {
        setRebrickableError(rebrickableResult.reason.message);
      }

      if (exportResult.status === 'fulfilled') {
        setExportImages(exportResult.value.images || []);
      }
    } finally {
      setRebrickableLoading(false);
      setExportImagesLoading(false);
    }
  };

  const handleRebrickableUpload = async () => {
    if (rebrickableUploadFile) {
      try {
        setRebrickableLoading(true);
        await api.uploadPartImage(part.setId, part.bagId, part.imageUrl, rebrickableUploadFile, parseInt(rebrickableUploadScale));
        setRebrickableModalOpen(false);
        setRebrickableUploadFile(null);
        setRebrickableUploadScale('100');
        setImageVersion(v => v + 1);
      } catch (error) {
        console.error('Error uploading image:', error);
        setRebrickableError('Failed to upload image. Please try again.');
      } finally {
        setRebrickableLoading(false);
      }
    }
  };

  const handleRebrickableScaleChange = async (newScale) => {
    setRebrickableScale(newScale);
    setRebrickableLoading(true);
    setRebrickableError(null);

    try {
      const result = await api.previewRebrickableImage(part.partId, newScale, { useLeftHalf });
      setRebrickablePreview(result);
    } catch (error) {
      setRebrickableError(error.message);
    } finally {
      setRebrickableLoading(false);
    }
  };

  const handleUseLeftHalfChange = async (checked) => {
    setUseLeftHalf(checked);
    setRebrickableLoading(true);
    setRebrickableError(null);
    try {
      const result = await api.previewRebrickableImage(part.partId, rebrickableScale, { useLeftHalf: checked });
      setRebrickablePreview(result);
    } catch (error) {
      setRebrickableError(error.message);
    } finally {
      setRebrickableLoading(false);
    }
  };

  const handleRebrickableReplace = async () => {
    setRebrickableLoading(true);
    try {
      await api.replaceImageFromRebrickable(part.partId, rebrickableScale, { useLeftHalf });
      setRebrickableModalOpen(false);
      setImageVersion(v => v + 1);
    } catch (error) {
      setRebrickableError(error.message);
    } finally {
      setRebrickableLoading(false);
    }
  };

  const handleExportImageSelect = async (imagePath) => {
    setRebrickableLoading(true);
    setRebrickableError(null);
    try {
      await api.useExportImage(part.partId, imagePath);
      setRebrickableModalOpen(false);
      setImageVersion(v => v + 1);
    } catch (error) {
      setRebrickableError(error.message);
    } finally {
      setRebrickableLoading(false);
    }
  };

  const [isHovered, setIsHovered] = useState(false);

  return (
    <>
      <Stack
        key={`${part.partId}-${part.color_id || 'nocolor'}`}
        data-part-key={`${part.partId}_${part.color_id || 'nocolor'}`}
        justify="flex-end"
        gap={4}
        align="center"
        onClick={() => setDetailOpen(true)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setDetailOpen(true); }
        }}
        role="button"
        tabIndex={0}
        aria-label={`${part.name || `Part ${part.partId}`}${part.colorName ? `, ${part.colorName}` : ''}: show details`}
        className={isHighlighted ? 'part-tile-highlight' : undefined}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{
          height: '100%',
          cursor: 'pointer',
          // Geen opacity/filter/transform op deze container: dat isoleert de
          // subtree, waardoor de mix-blend-mode van het plaatje niet meer
          // tegen de paneelachtergrond blendt (witte foto-achtergrond)
          transition: 'all 0.15s ease',
          position: 'relative',
          backgroundColor: isHovered ? 'rgba(34, 139, 230, 0.08)' : 'transparent',
          // padding + negatieve margin heffen elkaar op: geen layout-shift,
          // wel ruimte voor de hover-tint en highlight-ring rond de tegel.
          // NB: geen transform op hover - dat isoleert de subtree en breekt
          // de mix-blend-mode van het plaatje (witte foto-achtergrond)
          borderRadius: '6px',
          padding: '4px',
          margin: '-4px',
        }}
      >
        <Paper
          ref={containerRef}
          shadow="none"
          withBorder={false}
          style={{
            backgroundColor: "transparent",
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {imageLoadFailed ? (
            <MissingPartImage />
          ) : (
            <img
              ref={imgRef}
              src={imageUrl}
              alt={part.name || `LEGO part ${part.partId}`}
              className="part-grid-img"
              width={96}
              height={96}
              decoding="async"
              style={{
                imageRendering: 'auto',
                display: 'block',
                mixBlendMode: 'multiply',
                width: 96,
                height: 96,
                objectFit: 'contain',
              }}
              onError={handleImageError}
              onLoad={handleImageLoad}
            />
          )}
        </Paper>

        {adminMode && (
          <Group gap={2} justify="center" onClick={(e) => e.stopPropagation()}>
            <ActionIcon size="xs" color="blue" variant="filled" onClick={handleEditClick} aria-label={`Edit part ${part.partId}`}>
              <IconEdit size={12} />
            </ActionIcon>
            <ActionIcon size="xs" color="green" variant="filled" onClick={handleUploadClick} aria-label={`Upload a new image for part ${part.partId}`}>
              <IconUpload size={12} />
            </ActionIcon>
            <ActionIcon size="xs" color="cyan" variant="filled" onClick={handleRebrickableClick} aria-label={`Replace the image for part ${part.partId} with a Rebrickable image`}>
              <IconPhoto size={12} />
            </ActionIcon>
            <ActionIcon size="xs" color="grape" variant="filled" onClick={() => onMove(part)} aria-label={`Move part ${part.partId} to another bag`}>
              <IconArrowsExchange size={12} />
            </ActionIcon>
            <ActionIcon size="xs" color="orange" variant="filled" onClick={handleDisconnectClick} aria-label={`Disconnect part ${part.partId} and make its ID available for reuse`}>
              <IconUnlink size={12} />
            </ActionIcon>
            <ActionIcon size="xs" color="red" variant="filled" onClick={handleDeleteClick} aria-label={`Permanently delete part ${part.partId}`}>
              <IconTrash size={12} />
            </ActionIcon>
          </Group>
        )}

        <Stack align="center" gap={0} style={{ marginTop: 'auto' }}>
          <Text fw={700} size="sm">{part.quantity}x</Text>
          {part.name && (
            <Text size="xs" fw={500} ta="center" lineClamp={2} className="part-grid-name" style={{ color: '#495057', lineHeight: 1.25, maxWidth: 104 }}>
              {part.name}
            </Text>
          )}
          <Text c="dimmed" size="xs">{part.partId}</Text>
          {part.colorName && (
            <Group gap={4} align="center" wrap="nowrap" maw={104}>
              {part.colorHex && (
                <Box
                  style={{
                    width: 9, height: 9, borderRadius: '50%', flexShrink: 0,
                    backgroundColor: `#${part.colorHex.replace(/^#/, '')}`,
                    border: '1px solid rgba(0, 0, 0, 0.15)',
                  }}
                />
              )}
              <Text c="dimmed" size="xs" truncate>{part.colorName}</Text>
            </Group>
          )}
          <Group gap={4} align="center">
            {adminMode && svgStatus && svgStatus[part.partId] === true && (
              <Box
                style={{
                  width: 8, height: 8, borderRadius: '50%',
                  backgroundColor: '#51cf66', flexShrink: 0
                }}
                title="SVG file available in parts folder"
              />
            )}
            {adminMode && isDecoratedPart(part.part_ex_id) && (
              <Box
                style={{
                  width: 8, height: 8, borderRadius: '50%',
                  backgroundColor: '#9c36b5', flexShrink: 0
                }}
                title={`Decorated/printed part: ${part.part_ex_id}`}
              />
            )}
            {adminMode && multiItemCount && (
              <Badge
                size="xs" variant="filled" color="orange"
                style={{ padding: '0 4px', minWidth: 0 }}
                title={multiItemCount === true ? 'This part comes as a complete set' : `This part comes as a set of ${multiItemCount}`}
              >
                {multiItemCount === true ? 'SET' : `\u00d7${multiItemCount}`}
              </Badge>
            )}
          </Group>
        </Stack>
      </Stack>

      {adminMode && editModalOpen && (
        <Modal opened={editModalOpen} onClose={() => setEditModalOpen(false)} title="Edit Part ID & Quantity" size="sm">
          <Stack>
            <Text size="sm" c="dimmed">Current part_id: {part.partId}</Text>
            <Select
              label="Select new part_id from available parts (Bag 0)"
              placeholder="Choose a part"
              data={extraParts.map(ep => ({
                value: ep.part_id,
                label: `${ep.part_id} (${ep.part_ex_id}) - ${ep.color_name || 'No color'} - Qty: ${ep.quantity || 1}`
              }))}
              value={selectedPartId}
              onChange={setSelectedPartId}
              searchable
            />
            <NumberInput label="Quantity" placeholder="Enter quantity" value={selectedQuantity} onChange={setSelectedQuantity} min={1} max={999} allowDecimal={false} />
            <Group justify="flex-end" mt="md">
              <Button variant="light" onClick={() => setEditModalOpen(false)}>Cancel</Button>
              <Button leftSection={<IconCheck size={16} />} onClick={handleEditSave} disabled={!selectedPartId || (selectedPartId === part.partId && selectedQuantity === part.quantity)}>Save</Button>
            </Group>
          </Stack>
        </Modal>
      )}

      {adminMode && (
        <Modal
          opened={partConfirmAction !== null}
          onClose={() => setPartConfirmAction(null)}
          title={partConfirmAction === 'disconnect' ? 'Disconnect this part?' : 'Permanently delete this part?'}
          centered
          size="sm"
        >
          <Text size="sm" c="dimmed" mb="lg">
            {partConfirmAction === 'disconnect'
              ? `Part ${part.partId} will receive a temporary ID and its current ID will become available in bag 0.`
              : `Part ${part.partId} will be removed from this bag. This action cannot be undone.`}
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setPartConfirmAction(null)}>Cancel</Button>
            <Button
              color={partConfirmAction === 'disconnect' ? 'orange' : 'red'}
              onClick={() => {
                if (partConfirmAction === 'disconnect') onDisconnect(part.bagId, part.partId, part.color_id);
                else if (partConfirmAction === 'delete') onDelete(part.bagId, part.partId, part.color_id);
                setPartConfirmAction(null);
              }}
            >
              {partConfirmAction === 'disconnect' ? 'Disconnect part' : 'Delete part'}
            </Button>
          </Group>
        </Modal>
      )}

      {adminMode && uploadModalOpen && (
        <Modal
          opened={uploadModalOpen}
          onClose={() => { setUploadModalOpen(false); setUploadFile(null); setUploadScale('100'); }}
          title="Upload Part Image"
          size="sm"
        >
          <Stack>
            <Text size="sm" c="dimmed">Upload a new image for part: {part.partId}</Text>
            <FileInput label="Select image file" placeholder="Choose an image (PNG, JPG, JPEG, SVG)" accept="image/png,image/jpeg,image/jpg,image/svg+xml" value={uploadFile} onChange={setUploadFile} leftSection={<IconUpload size={16} />} />
            <Select
              label="Scale image (PNG/JPG only)"
              description="Resize before saving - useful for large images"
              data={[
                { value: '100', label: '100% (original)' },
                { value: '75', label: '75%' },
                { value: '50', label: '50%' },
                { value: '25', label: '25%' },
              ]}
              value={uploadScale}
              onChange={setUploadScale}
            />
            <Text size="xs" c="dimmed">Max file size: 5MB. Uploading SVG will replace existing PNG and update database.</Text>
            <Group justify="flex-end" mt="md">
              <Button variant="light" onClick={() => { setUploadModalOpen(false); setUploadFile(null); setUploadScale('100'); }}>Cancel</Button>
              <Button leftSection={<IconUpload size={16} />} onClick={handleUploadSave} disabled={!uploadFile} color="green">Upload</Button>
            </Group>
          </Stack>
        </Modal>
      )}

      {adminMode && rebrickableModalOpen && (
        <Modal
          opened={rebrickableModalOpen}
          onClose={() => { setRebrickableModalOpen(false); setRebrickablePreview(null); setRebrickableError(null); setExportImages([]); setSelectedExportImage(null); }}
          title="Replace Image"
          size="lg"
        >
          <Stack>
            <Text size="sm" c="dimmed">Part: {part.partId} ({part.part_ex_id})</Text>

            {rebrickableLoading && !rebrickablePreview && (
              <Center py="xl"><Loader size="lg" /></Center>
            )}

            {rebrickableError && (
              <Alert color="red" title="Error">{rebrickableError}</Alert>
            )}

            {rebrickablePreview && (
              <>
                <Group justify="center" gap="xl">
                  <Stack align="center" gap="xs">
                    <Text size="sm" fw={500}>Current</Text>
                    <Box style={{ border: '1px solid #dee2e6', borderRadius: 8, padding: 20, backgroundColor: '#f8f9fa', minWidth: 200, minHeight: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <img src={imageUrl} alt="Current" style={{ maxWidth: 180, maxHeight: 180, imageRendering: 'pixelated' }} />
                    </Box>
                  </Stack>

                  <Text size="xl" c="dimmed">{'\u2192'}</Text>

                  <Stack align="center" gap="xs">
                    <Text size="sm" fw={500}>New ({rebrickablePreview.imageSource === 'bricklink' ? 'BrickLink' : 'Rebrickable'})</Text>
                    <Box style={{ border: '2px solid #228be6', borderRadius: 8, padding: 20, backgroundColor: '#e7f5ff', minWidth: 200, minHeight: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                      <img src={rebrickablePreview.preview} alt="New" style={{ maxWidth: 180, maxHeight: 180, imageRendering: 'pixelated' }} />
                      {rebrickableLoading && (
                        <Box style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(255,255,255,0.7)' }}>
                          <Loader size="sm" />
                        </Box>
                      )}
                    </Box>
                  </Stack>
                </Group>

                <Text size="xs" c="dimmed" ta="center">
                  Size: {rebrickablePreview.targetSize.width} x {rebrickablePreview.targetSize.height}px
                  {rebrickablePreview.isMinifigPart && ` (Minifig part - fixed size)`}
                  {rebrickablePreview.categoryId && !rebrickablePreview.isMinifigPart && ` (Category: ${rebrickablePreview.categoryId})`}
                </Text>

                {!rebrickablePreview.isMinifigPart && (
                  <Box>
                    <Text size="sm" fw={500} mb={4}>Scale: {rebrickableScale}%</Text>
                    <Slider
                      value={rebrickableScale} onChange={handleRebrickableScaleChange}
                      min={5} max={100} step={5}
                      marks={[{ value: 10, label: '10%' }, { value: 20, label: '20%' }, { value: 50, label: '50%' }, { value: 100, label: '100%' }]}
                      disabled={rebrickableLoading}
                    />
                  </Box>
                )}

                {rebrickablePreview.imageSource === 'bricklink' && (
                  <Checkbox label="Use left half only (for front+back images)" checked={useLeftHalf} onChange={(e) => handleUseLeftHalfChange(e.currentTarget.checked)} disabled={rebrickableLoading} />
                )}
              </>
            )}

            <Group justify="flex-end" mt="md">
              <Button variant="light" onClick={() => { setRebrickableModalOpen(false); setRebrickablePreview(null); setRebrickableError(null); setExportImages([]); setSelectedExportImage(null); }}>Cancel</Button>
              <Button leftSection={<IconCheck size={16} />} onClick={handleRebrickableReplace} disabled={!rebrickablePreview || rebrickableLoading} loading={rebrickableLoading} color="cyan">
                Use {rebrickablePreview?.imageSource === 'bricklink' ? 'BrickLink' : 'Rebrickable'}
              </Button>
            </Group>

            <Divider my="md" label="Or upload your own image" labelPosition="center" />

            <Group align="flex-end" gap="sm">
              <FileInput label="Upload image (e.g. from remove.bg)" placeholder="Choose an image" accept="image/png,image/jpeg,image/jpg,image/svg+xml" value={rebrickableUploadFile} onChange={setRebrickableUploadFile} leftSection={<IconUpload size={16} />} style={{ flex: 1 }} />
              <Select label="Scale" data={[{ value: '100', label: '100%' }, { value: '75', label: '75%' }, { value: '50', label: '50%' }, { value: '25', label: '25%' }]} value={rebrickableUploadScale} onChange={setRebrickableUploadScale} style={{ width: 90 }} />
              <Button leftSection={<IconUpload size={16} />} onClick={handleRebrickableUpload} disabled={!rebrickableUploadFile || rebrickableLoading} loading={rebrickableLoading} color="green">Upload</Button>
            </Group>

            {exportImagesLoading ? (
              <Center py="md"><Loader size="sm" /></Center>
            ) : exportImages.length > 0 && (
              <>
                <Divider my="md" label="Or use exported image" labelPosition="center" />
                <Text size="sm" c="dimmed" mb="xs">Found {exportImages.length} image{exportImages.length !== 1 ? 's' : ''} from processing:</Text>
                <Group gap="sm" wrap="wrap">
                  {exportImages.map((img, idx) => (
                    <Box
                      key={idx}
                      onClick={() => !rebrickableLoading && handleExportImageSelect(img.path)}
                      style={{
                        border: selectedExportImage === img.path ? '2px solid #40c057' : '1px solid #dee2e6',
                        borderRadius: 8, padding: 8, backgroundColor: '#f8f9fa',
                        cursor: rebrickableLoading ? 'not-allowed' : 'pointer',
                        opacity: rebrickableLoading ? 0.5 : 1, transition: 'all 0.2s',
                      }}
                      onMouseEnter={(e) => { if (!rebrickableLoading) e.currentTarget.style.borderColor = '#40c057'; }}
                      onMouseLeave={(e) => { if (selectedExportImage !== img.path) e.currentTarget.style.borderColor = '#dee2e6'; }}
                    >
                      <Stack gap={4} align="center">
                        <img src={api.getExportImageUrl(img.path)} alt={`Export ${idx + 1}`} style={{ maxWidth: 80, maxHeight: 80, imageRendering: 'pixelated' }} />
                        <Text size="xs" c="dimmed">{img.source}</Text>
                        <Text size="xs" c="dimmed">{img.width}x{img.height}</Text>
                      </Stack>
                    </Box>
                  ))}
                </Group>
              </>
            )}
          </Stack>
        </Modal>
      )}

      {detailOpen && (
        <Modal
          opened={detailOpen} onClose={() => setDetailOpen(false)}
          size="lg" centered padding={0} radius="lg" withCloseButton={false}
          fullScreen={window.innerWidth < 600}
          styles={{ content: { overflow: 'hidden' }, body: { padding: 0 } }}
        >
          <Box style={{ position: 'relative' }}>
            {part.has_3d ? (
              <LDrawViewer
                ref={viewer3dRef}
                partNum={part.ldraw_file || part.bl_part_id}
                blColorId={part.bl_color_id}
                colorName={part.colorName}
                height={window.innerWidth < 600 ? Math.max(240, window.innerHeight - 320) : 340}
              />
            ) : (
              <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: window.innerWidth < 600 ? Math.max(240, window.innerHeight - 320) : 340, backgroundColor: '#fff' }}>
                {imageLoadFailed ? (
                  <MissingPartImage size={220} />
                ) : (
                  <img
                    src={getThumbUrl(part.imageUrl, 512)}
                    alt={part.name || `Part ${part.partId}`}
                    style={{ maxWidth: '70%', maxHeight: '85%', mixBlendMode: 'multiply' }}
                    onError={handleImageError}
                  />
                )}
              </Box>
            )}
            <Group gap={6} style={{ position: 'absolute', top: 12, right: 12, zIndex: 10 }}>
              {part.has_3d && (
                <Tooltip label="Reset view" withArrow position="bottom">
                  <ActionIcon variant="white" color="gray" size="md" radius="xl" aria-label="Reset 3D view" onClick={() => viewer3dRef.current?.resetView()} style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                    <IconRotate size={16} />
                  </ActionIcon>
                </Tooltip>
              )}
              <ActionIcon variant="white" color="gray" size="md" radius="xl" onClick={() => setDetailOpen(false)} aria-label="Close" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                <Text size="sm" fw={600} lh={1}>&times;</Text>
              </ActionIcon>
            </Group>
          </Box>

          <Box px="lg" py="md" style={{ borderTop: '1px solid #e9ecef', backgroundColor: '#fff' }}>
            <Group justify="space-between" align="flex-start" wrap="nowrap" gap="sm">
              <Box style={{ minWidth: 0 }}>
                {part.name && <Text fw={700} size="md" lh={1.3}>{part.name}</Text>}
                <Group gap={6} mt={4}>
                  {part.colorHex && (
                    <Box style={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: `#${part.colorHex.replace(/^#/, '')}`, border: '1px solid rgba(0,0,0,0.15)', flexShrink: 0 }} />
                  )}
                  {part.colorName && <Text size="sm" c="dimmed">{part.colorName}</Text>}
                </Group>
              </Box>
              <Badge size="lg" variant="light" color="blue" radius="xl" style={{ flexShrink: 0, textTransform: 'none' }}>
                {part.quantity}x
              </Badge>
            </Group>

            <Group gap="lg" mt="sm">
              <Box>
                <Text size="xs" c="dimmed" fw={600} tt="uppercase" lts="0.04em">Element ID</Text>
                <Text size="sm" fw={600}>{part.partId}</Text>
              </Box>
              {part.bl_part_id && (
                <Box>
                  <Text size="xs" c="dimmed" fw={600} tt="uppercase" lts="0.04em">BrickLink</Text>
                  <Text size="sm" fw={600}>{part.bl_part_id}</Text>
                </Box>
              )}
            </Group>

            {partLocations && partLocations.length > 0 && (
              <Box mt="sm">
                <Text size="xs" c="dimmed" fw={600} tt="uppercase" lts="0.04em" mb={4}>In this set</Text>
                <Group gap={6}>
                  {partLocations.map((loc) => (
                    <Badge
                      key={loc.bagNumber}
                      component={onJumpToBag ? 'button' : 'span'}
                      type={onJumpToBag ? 'button' : undefined}
                      variant="light" radius="xl" size="lg"
                      style={{ textTransform: 'none', cursor: onJumpToBag ? 'pointer' : 'default', backgroundColor: '#f1f3f5', color: '#495057' }}
                      onClick={onJumpToBag ? () => { setDetailOpen(false); onJumpToBag(loc.bagNumber, `${part.partId}_${part.color_id || 'nocolor'}`); } : undefined}
                    >
                      {loc.chipLabel} · {loc.quantity}x
                    </Badge>
                  ))}
                </Group>
              </Box>
            )}

            <Group gap="xs" mt="md">
              <Button
                component="a" size="xs" radius="xl" variant="light" color="gray"
                href={`https://rebrickable.com/parts/${part.part_ex_id}/x/${part.color_id}/`}
                target="_blank" rel="noopener noreferrer"
                leftSection={<IconSquareLetterR size={14} />}
              >
                Rebrickable
              </Button>
              {part.bl_part_id && part.bl_color_id && (
                <Button
                  component="a" size="xs" radius="xl" variant="light" color="gray"
                  href={`https://www.bricklink.com/v2/catalog/catalogitem.page?P=${part.bl_part_id}&C=${part.bl_color_id}`}
                  target="_blank" rel="noopener noreferrer"
                  leftSection={<IconSquareLetterB size={14} />}
                >
                  BrickLink
                </Button>
              )}
            </Group>
          </Box>
        </Modal>
      )}
    </>
  );
});

// Herkenbaarheid van een ingeklapte bag: het bag-modelplaatje (als dat er is)
// plus één part per kleur, zodat "de tan bag" en "de donkerrode bag" in één
// oogopslag te onderscheiden zijn. Decoratief (aria-hidden): de aantallen
// ernaast dragen de info. Lazy mag hier - deze plaatjes tellen niet mee voor
// de staggered bag loader.
const BAG_STRIP_MAX = 6;
const BagThumbStrip = memo(function BagThumbStrip({ parts, previewImage }) {
  const picks = useMemo(() => {
    const list = parts || [];
    const seenColors = new Set();
    const chosen = [];
    for (const part of list) {
      if (chosen.length >= BAG_STRIP_MAX) break;
      if (!part.imageUrl) continue;
      const color = part.color_id ?? part.color ?? 'none';
      if (seenColors.has(color)) continue;
      seenColors.add(color);
      chosen.push(part);
    }
    // Weinig kleuren: aanvullen met de volgende parts
    for (const part of list) {
      if (chosen.length >= BAG_STRIP_MAX) break;
      if (part.imageUrl && !chosen.includes(part)) chosen.push(part);
    }
    return chosen;
  }, [parts]);

  if (!previewImage && picks.length === 0) return null;
  return (
    <div className="bag-thumb-strip" aria-hidden="true">
      {previewImage && (
        <img className="bag-thumb-strip-preview" src={getThumbUrl(previewImage, 96)} alt="" loading="lazy" decoding="async" />
      )}
      {picks.map(part => (
        <img
          key={`${part.partId}_${part.color_id || 'nocolor'}`}
          className="bag-thumb-strip-part"
          src={getThumbUrl(part.imageUrl, 96)}
          alt=""
          loading="lazy"
          decoding="async"
        />
      ))}
    </div>
  );
});

const BagAccordionItem = memo(function BagAccordionItem({ bag, isOpen, adminMode, onDisconnectPart, onDeletePart, onEditPart, onQuantityEditPart, onMovePart, onEditDisplayNumbers, onUploadPreviewImage, onDeletePreviewImage, onAddPart, onDeleteBag, extraParts, setId, svgStatus, showOnlyDecoratedParts, showOnlyMissingImages, showOnlyMultiItemParts, multiItemParts, failedImages, onImageError, shouldLoad, isLoaded, onBagImageLoad, partLocations, onJumpToBag, highlightedPartKeys, partOrder = 'color' }) {
  const [addPartModalOpen, setAddPartModalOpen] = useState(false);
  const [addPartId, setAddPartId] = useState('');
  const [addPartQuantity, setAddPartQuantity] = useState(1);
  const [addPartLoading, setAddPartLoading] = useState(false);
  const [addPartError, setAddPartError] = useState(null);

  const handleAddPartSave = async () => {
    if (!addPartId.trim()) return;
    setAddPartLoading(true);
    setAddPartError(null);
    try {
      await onAddPart(bag.id, addPartId.trim(), addPartQuantity);
      setAddPartModalOpen(false);
      setAddPartId('');
      setAddPartQuantity(1);
    } catch (error) {
      setAddPartError(error.message);
    } finally {
      setAddPartLoading(false);
    }
  };

  const [previewUploading, setPreviewUploading] = useState(false);
  const previewFileInputRef = useRef(null);
  const previewBoxRef = useRef(null);

  const handlePreviewFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await uploadPreviewFile(file);
  };

  const uploadPreviewFile = async (file) => {
    setPreviewUploading(true);
    try {
      await onUploadPreviewImage(bag.id, file);
    } finally {
      setPreviewUploading(false);
      if (previewFileInputRef.current) {
        previewFileInputRef.current.value = '';
      }
    }
  };

  const handlePreviewPaste = async (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of items) {
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const file = item.getAsFile();
        if (file) {
          await uploadPreviewFile(file);
        }
        break;
      }
    }
  };

  const firstPart = bag.parts?.[0];
  const firstPartImageUrl = firstPart?.imageUrl ? getImageUrl(firstPart.imageUrl) : null;

  const [loadedImageCount, setLoadedImageCount] = useState(0);

  // Coalesce image-load reports: op een warme cache vuren de load-events van
  // honderden parts in één burst, en één setState per event geeft honderden
  // commits rug-aan-rug - React's infinite-loop-detectie (error #185) slaat
  // daar vals op aan. Eén flush per ~30ms is ruim genoeg voor de
  // skeleton-teller en de staggered loader.
  const loadedCountRef = useRef(0);
  const loadFlushTimer = useRef(null);
  const handlePartImageLoad = useCallback(() => {
    loadedCountRef.current += 1;
    if (loadFlushTimer.current) return;
    loadFlushTimer.current = setTimeout(() => {
      loadFlushTimer.current = null;
      setLoadedImageCount(loadedCountRef.current);
    }, 30);
  }, []);
  useEffect(() => () => {
    if (loadFlushTimer.current) {
      clearTimeout(loadFlushTimer.current);
      loadFlushTimer.current = null; // anders blokkeert de stale id elke volgende flush
    }
  }, []);

  const detectedBgColor = useImageBackgroundColor(firstPartImageUrl);
  const accordionBgColor = isOpen ? detectedBgColor : '#f8f9fa';
  const textColor = isColorLight(accordionBgColor) ? '#000000' : '#FFFFFF';

  const itemStyle = useMemo(() => {
    const style = {
      position: 'relative',
      ...(isOpen ? {
        backgroundColor: '#f8f9fa',
        transition: 'background-color 0.2s ease-in-out',
        borderRadius: 'var(--mantine-radius-md)',
      } : {}),
    };
    if (bag.leftover) {
      // De left over-groep is een bijlage, geen "volgende zak": gestippelde
      // rand + lichte tint zodat hij visueel losstaat van de gewone groups
      style.border = '1px dashed #ced4da';
      style.borderRadius = 'var(--mantine-radius-md)';
    }
    return style;
  }, [isOpen, bag.leftover]);

  // Stable part objects (with bag/set context merged in) so PartGridItem's
  // shallow memo isn't defeated by a fresh object literal every render
  const partsWithContext = useMemo(
    () => (bag.parts || []).map(p => ({ ...p, bagId: bag.id, setId })),
    [bag.parts, bag.id, setId]
  );

  const filteredParts = useMemo(() => {
    if (!partsWithContext.length) return [];
    if (!showOnlyDecoratedParts && !showOnlyMissingImages && !showOnlyMultiItemParts) {
      return partsWithContext;
    }
    return partsWithContext.filter(part => {
      const partKey = `${bag.id}_${part.partId}_${part.color_id || 'nocolor'}`;
      const isDecorated = showOnlyDecoratedParts && isDecoratedPart(part.part_ex_id);
      const hasFailedImage = showOnlyMissingImages && failedImages && failedImages[partKey];
      const isMultiItem = showOnlyMultiItemParts && multiItemParts && multiItemParts[part.partId];
      return isDecorated || hasFailedImage || isMultiItem;
    });
  }, [bag.id, partsWithContext, showOnlyDecoratedParts, showOnlyMissingImages, showOnlyMultiItemParts, failedImages, multiItemParts]);

  // "Sort by part type": families (Bricks, Slopes, Plates, ...) with a header
  // each, sorted by size inside. Default 'color' keeps the backend order.
  const partGroups = useMemo(
    () => (partOrder === 'type' ? groupPartsByType(filteredParts) : null),
    [partOrder, filteredParts]
  );

  const totalImages = bag.parts?.length || 0;


  // Only report completion once: the parent just advances the staggered loader
  // on loaded >= total. Without the guard every loadedImageCount increment past
  // total (found-section remounts keep counting) re-reports and re-renders the
  // whole page.
  const hasReportedLoad = useRef(false);
  useEffect(() => {
    // Reset bij een andere bag (instantie-hergebruik bij set→set-navigatie)
    hasReportedLoad.current = false;
  }, [bag.id]);
  useEffect(() => {
    if (hasReportedLoad.current) return;
    if (shouldLoad && onBagImageLoad && loadedImageCount >= totalImages) {
      hasReportedLoad.current = true;
      onBagImageLoad(bag.bagNumber, loadedImageCount, totalImages);
    }
  }, [loadedImageCount, totalImages, shouldLoad, onBagImageLoad, bag.bagNumber]);

  const bagStats = useMemo(() => ({
    uniqueParts: bag.parts?.length || 0,
    totalPieces: bag.parts?.reduce((sum, part) => sum + (part.quantity || 0), 0) || 0,
    allPartsAreSvg: adminMode && bag.parts && bag.parts.length > 0 && svgStatus
      ? bag.parts.every(part => svgStatus[part.partId] === true)
      : false
  }), [bag.parts, adminMode, svgStatus]);

  return (
    <Accordion.Item
      key={bag.bagNumber}
      value={`bag-${bag.bagNumber}`}
      style={itemStyle}
      data-bag-id={`bag-${bag.bagNumber}`}
    >
      <Box pos="relative">
        <Accordion.Control>
          <Group justify="space-between" wrap="nowrap" className="bag-control-row" style={{ width: '100%', paddingRight: '56px' }}>
            <Group gap="xs" align="center" style={{ flexShrink: 0 }}>
              <Group gap={0} align="center">
                {bag.displayNumbers && bag.displayNumbers.length > 0 ? (
                  bag.displayNumbers.map((num, idx) => (
                    <Box key={num} style={{ marginLeft: idx > 0 ? '-75px' : 0 }}>
                      {!isNaN(parseInt(num))
                        ? <LegoBag bagNumber={num} height={54} isVirtual={bag.virtual} />
                        : <Text fw={600} size="sm" style={{ color: '#495057', marginLeft: '20px' }}>{num}</Text>
                      }
                    </Box>
                  ))
                ) : (
                  <Box>
                    {!isNaN(parseInt(bag.bagNumber))
                      // Breedte = zichtbare zak (200:220 bij 54px hoog); de standaard
                      // 200px-SVG liet ~90px lege ruimte achter die de strip nodig heeft
                      ? <Box ml={17}><LegoBag bagNumber={bag.bagNumber} height={54} width={49} noMargin isVirtual={bag.virtual} /></Box>
                      : bag.bagNumber === 'Extra' ? (
                        <Group gap={6} align="center" style={{ marginLeft: '20px' }}>
                          <Text fw={600} size="sm" style={{ color: '#495057' }}>{bag.bagNumber}</Text>
                          <Tooltip
                            label="Parts not shown in any building step (such as minifigure parts, accessories, and loose elements) are grouped here, even if they may be physically located in a different bag."
                            multiline w={280} withArrow position="right"
                          >
                            <IconInfoCircle size={16} style={{ color: '#868e96', cursor: 'help' }} />
                          </Tooltip>
                        </Group>
                      ) : bag.leftover ? (
                        <Group gap={6} align="center" style={{ marginLeft: '20px' }}>
                          <Text fw={600} size="sm" style={{ color: '#868e96' }}>{bag.bagNumber}</Text>
                          <Badge size="xs" variant="light" color="gray" radius="sm" style={{ textTransform: 'none', flexShrink: 0 }}>
                            not needed
                          </Badge>
                          <Tooltip
                            label="Parts of the set that this build doesn't use. Set them aside while sorting."
                            multiline w={260} withArrow position="right"
                          >
                            <IconInfoCircle size={16} style={{ color: '#868e96', cursor: 'help' }} />
                          </Tooltip>
                        </Group>
                      ) : (
                        <Text fw={600} size="sm" style={{ color: '#495057', marginLeft: '20px' }}>{bag.bagNumber}</Text>
                      )
                    }
                  </Box>
                )}
              </Group>
              {bag.name && bag.name !== bag.bagNumber && (
                <Text fw={600} size="sm" style={{ color: bag.leftover ? '#868e96' : '#495057' }}>
                  {bag.name}
                </Text>
              )}
              {adminMode && (
                <Box
                  style={{
                    width: 10, height: 10, borderRadius: '50%',
                    backgroundColor: bagStats.allPartsAreSvg ? '#51cf66' : '#fd7e14',
                    flexShrink: 0
                  }}
                  title={bagStats.allPartsAreSvg ? 'All parts have SVG files' : 'Some parts still need SVG conversion'}
                />
              )}
              {adminMode && !isNaN(parseInt(bag.bagNumber)) && (
                <ActionIcon
                  component="span" role="button" tabIndex={0} size="xs" variant="subtle" color="gray"
                  aria-label={`Edit display numbers for bag ${bag.bagNumber}`}
                  onClick={(e) => { e.stopPropagation(); onEditDisplayNumbers(bag); }}
                  onKeyDown={(e) => activateOnKeyboard(e, () => onEditDisplayNumbers(bag))}
                >
                  <IconEdit size={14} />
                </ActionIcon>
              )}
              {adminMode && (
                <ActionIcon
                  component="span" role="button" tabIndex={0} size="xs" variant="subtle" color="green"
                  aria-label={`Add a part to bag ${bag.bagNumber}`}
                  onClick={(e) => { e.stopPropagation(); setAddPartModalOpen(true); setAddPartError(null); }}
                  onKeyDown={(e) => activateOnKeyboard(e, () => { setAddPartModalOpen(true); setAddPartError(null); })}
                >
                  <IconPlus size={14} />
                </ActionIcon>
              )}
              {adminMode && (
                <ActionIcon
                  component="span" role="button" tabIndex={0} size="xs" variant="subtle" color="red"
                  aria-label={`Delete bag ${bag.bagNumber}`}
                  onClick={(e) => { e.stopPropagation(); onDeleteBag(bag.id, bag.bagNumber); }}
                  onKeyDown={(e) => activateOnKeyboard(e, () => onDeleteBag(bag.id, bag.bagNumber))}
                >
                  <IconTrash size={14} />
                </ActionIcon>
              )}
            </Group>

            {!isOpen && !adminMode && <BagThumbStrip parts={bag.parts} previewImage={bag.previewImage} />}

            {(
              <Group gap="sm" wrap="nowrap" className="bag-stats" style={{ flexShrink: 0 }}>
                <Text size="xs" c="dimmed"><Text component="span" fw={600} c="dark">{bagStats.uniqueParts}</Text> unique</Text>
                <Text size="xs" c="dimmed" className="bag-stats-sep">{'\u2022'}</Text>
                <Text size="xs" c="dimmed"><Text component="span" fw={600} c="dark">{bagStats.totalPieces}</Text> pcs</Text>
              </Group>
            )}
          </Group>
        </Accordion.Control>
        {/* A sibling of Accordion.Control: placing this button inside the
            control creates a nested interactive element that screen readers
            cannot announce or activate reliably. */}
        <Tooltip label="Download as .bsx (BrickStore/BrickStock)" withArrow>
          <ActionIcon
            aria-label={`Download bag ${bag.bagNumber} as BSX`}
            size="sm"
            variant="subtle"
            color="gray"
            onClick={() => downloadBagBsx(setId, bag)}
            style={{ position: 'absolute', top: '50%', transform: 'translateY(-50%)', right: 42, zIndex: 2 }}
          >
            <IconDownload size={16} />
          </ActionIcon>
        </Tooltip>
      </Box>
      <Accordion.Panel style={{ backgroundColor: '#f8f9fa' }}>
        {isOpen ? <>
          {bag.leftover && (
            <Text size="sm" c="dimmed" mb="sm">
              These parts are in the box but this build doesn&apos;t use them.
              You don&apos;t need to collect them; check them only to make sure nothing is missing.
            </Text>
          )}
          {!shouldLoad && (
            <Box style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'flex-end' }}>
              {Array.from({ length: Math.min(totalImages, 12) }).map((_, i) => (
                <Skeleton key={i} width={80} height={80} radius="md" animate={true} />
              ))}
              {totalImages > 12 && (<Text size="sm" c="dimmed">+{totalImages - 12} more...</Text>)}
            </Box>
          )}

          {/* Afbeeldingen krijgen vaste 96x96-vakken en staan meteen in de flow.
              Zo springt een complete zak niet van offscreen naar zijn echte hoogte. */}
          <Box style={{ position: 'relative', textAlign: 'left' }}>
            {bag.previewImage && (
              <Box style={{ float: 'left', marginRight: '12px', marginBottom: '8px', width: 'clamp(110px, 100%, 300px)', position: 'relative', borderRadius: 'var(--mantine-radius-md)', backgroundColor: '#d4e5f7', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
                <Image src={getImageUrl(bag.previewImage)} alt={`Bag ${bag.bagNumber} preview`} radius="md" style={{ padding: '8px' }} />
                {adminMode && (
                  <ActionIcon
                    size="xs" color="red" variant="filled"
                    aria-label={`Delete preview image for bag ${bag.bagNumber}`}
                    style={{ position: 'absolute', top: 12, right: 12 }}
                    onClick={() => onDeletePreviewImage && onDeletePreviewImage(bag.id)}
                  >
                    <IconTrash size={12} />
                  </ActionIcon>
                )}
              </Box>
            )}
            {adminMode && !bag.previewImage && (
              <Box
                ref={previewBoxRef} tabIndex={0} role="button" aria-label={`Upload preview image for bag ${bag.bagNumber}`}
                style={{
                  float: 'left', marginRight: '8px', marginBottom: '4px', width: '120px', height: '90px',
                  backgroundColor: previewUploading ? '#e9ecef' : '#f8f9fa',
                  borderRadius: 'var(--mantine-radius-md)', border: '2px dashed #adb5bd',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '4px',
                  cursor: previewUploading ? 'wait' : 'pointer', transition: 'all 0.2s', outline: 'none',
                }}
                onClick={() => !previewUploading && previewFileInputRef.current?.click()}
                onKeyDown={(e) => {
                  if (!previewUploading && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault(); previewFileInputRef.current?.click();
                  }
                }}
                onPaste={handlePreviewPaste}
                onFocus={(e) => e.currentTarget.style.borderColor = '#228be6'}
                onBlur={(e) => e.currentTarget.style.borderColor = '#adb5bd'}
                onMouseOver={(e) => !previewUploading && (e.currentTarget.style.borderColor = '#228be6')}
                onMouseOut={(e) => !e.currentTarget.contains(document.activeElement) && (e.currentTarget.style.borderColor = '#adb5bd')}
              >
                <input ref={previewFileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handlePreviewFileChange} />
                {previewUploading ? (
                  <Loader size="sm" />
                ) : (
                  <>
                    <IconUpload size={24} color="#adb5bd" />
                    <Text size="xs" c="dimmed" ta="center">Click or paste</Text>
                  </>
                )}
              </Box>
            )}
            {shouldLoad && (() => {
              const renderPart = (part, index, keyPrefix = '') => (
                <Box
                  key={`${part.partId}-${part.color_id || 'nocolor'}-${keyPrefix}${index}`}
                  className="part-grid-cell"
                  style={{ display: 'inline-block', verticalAlign: 'bottom', marginRight: '8px', marginBottom: '8px' }}
                >
                  <PartGridItem
                    part={part}
                    bgColor={accordionBgColor} adminMode={adminMode}
                    onDisconnect={onDisconnectPart} onDelete={onDeletePart} onEdit={onEditPart} onQuantityEdit={onQuantityEditPart}
                    onMove={onMovePart}
                    extraParts={extraParts} svgStatus={svgStatus}
                    multiItemCount={multiItemParts && multiItemParts[part.partId]}
                    onImageError={onImageError} onImageLoad={handlePartImageLoad}
                    partLocations={partLocations && partLocations[`${part.partId}_${part.color_id || 'nocolor'}`]}
                    onJumpToBag={onJumpToBag}
                    isHighlighted={highlightedPartKeys?.includes(`${part.partId}_${part.color_id || 'nocolor'}`)}
                  />
                </Box>
              );
              if (!partGroups) return filteredParts.map((part, index) => renderPart(part, index));
              return partGroups.map((group, gi) => (
                <Fragment key={group.key}>
                  <Box
                    role="heading" aria-level={4}
                    style={{
                      display: 'flex', alignItems: 'baseline', gap: 8,
                      margin: gi === 0 ? '0 0 8px' : '10px 0 8px',
                      paddingBottom: 4, borderBottom: '1px solid #dee2e6',
                    }}
                  >
                    <Text fw={700} size="sm" style={{ color: '#343a40' }}>{group.label}</Text>
                    <Text size="xs" c="dimmed">
                      {group.parts.length} {group.parts.length === 1 ? 'part' : 'parts'} · {group.pieces} {group.pieces === 1 ? 'piece' : 'pieces'}
                    </Text>
                  </Box>
                  {group.parts.map((part, index) => renderPart(part, index, `${group.key}-`))}
                </Fragment>
              ));
            })()}
          </Box>

        </> : null}
      </Accordion.Panel>

      {adminMode && addPartModalOpen && (
        <Modal
          opened={addPartModalOpen}
          onClose={() => { setAddPartModalOpen(false); setAddPartId(''); setAddPartQuantity(1); setAddPartError(null); }}
          title={`Add part to bag ${bag.bagNumber}`} size="sm"
        >
          <Stack>
            <TextInput label="Element ID" placeholder="e.g. 6328389" value={addPartId} onChange={(e) => setAddPartId(e.target.value)} description="Must already exist in this set (e.g. in bag 0)" autoFocus onKeyDown={(e) => { if (e.key === 'Enter' && addPartId.trim()) handleAddPartSave(); }} />
            <NumberInput label="Quantity" value={addPartQuantity} onChange={setAddPartQuantity} min={1} max={999} allowDecimal={false} />
            {addPartError && (<Alert color="red" title="Error">{addPartError}</Alert>)}
            <Group justify="flex-end" mt="md">
              <Button variant="light" onClick={() => { setAddPartModalOpen(false); setAddPartId(''); setAddPartQuantity(1); setAddPartError(null); }}>Cancel</Button>
              <Button leftSection={<IconPlus size={16} />} onClick={handleAddPartSave} disabled={!addPartId.trim()} loading={addPartLoading} color="green">Add Part</Button>
            </Group>
          </Stack>
        </Modal>
      )}
    </Accordion.Item>
  );
}, (prevProps, nextProps) => {
  const baseEqual = (
    prevProps.bag.id === nextProps.bag.id &&
    prevProps.bag.previewImage === nextProps.bag.previewImage &&
    prevProps.isOpen === nextProps.isOpen &&
    prevProps.adminMode === nextProps.adminMode &&
    prevProps.svgStatus === nextProps.svgStatus &&
    prevProps.extraParts === nextProps.extraParts &&
    prevProps.showOnlyDecoratedParts === nextProps.showOnlyDecoratedParts &&
    prevProps.showOnlyMissingImages === nextProps.showOnlyMissingImages &&
    prevProps.showOnlyMultiItemParts === nextProps.showOnlyMultiItemParts &&
    prevProps.multiItemParts === nextProps.multiItemParts &&
    prevProps.failedImages === nextProps.failedImages &&
    prevProps.shouldLoad === nextProps.shouldLoad &&
    prevProps.isLoaded === nextProps.isLoaded &&
    prevProps.partOrder === nextProps.partOrder
  );
  if (!baseEqual) return false;
  // Highlight-key: alleen opnieuw renderen als de (oude of nieuwe) key in
  // déze zak zit - andere zakken hoeven niet mee te schilderen
  if (prevProps.highlightedPartKeys !== nextProps.highlightedPartKeys) {
    const touchesBag = (keys) => (keys || []).some(key => (nextProps.bag.parts || []).some(p => `${p.partId}_${p.color_id || 'nocolor'}` === key));
    if (touchesBag(prevProps.highlightedPartKeys) || touchesBag(nextProps.highlightedPartKeys)) return false;
  }
  return true;
});

function SetDetailPageContent({ initialShop, initialSet }) {
  const { id } = useParams();
  const searchParams = useSearchParams();
  const catalogShop = initialShop || initialSet?.catalog_shop || 'da-DK';
  const router = useRouter();
  const { user, loading: authLoading } = useAuth();
  const adminRequested = searchParams.get('admin') === 'true';
  const adminMode = adminRequested && !!user;
  // De publieke SSR-inhoud hoeft niet te wachten op de losse admin-sessiecheck.
  // Alleen een expliciete ?admin=true-route wacht, omdat die extra velden nodig heeft.
  const waitingForAdminAuth = adminRequested && authLoading;

  // Server-fetched set: content is in the initial HTML, so regular visitors
  // need no client fetch (admin mode refetches with auth for extra fields)
  const hasInitialSet = !!(initialSet && initialSet.id === id);
  const [set, setSet] = useState(hasInitialSet ? initialSet : null);
  const [loading, setLoading] = useState(!hasInitialSet);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [extraParts, setExtraParts] = useState([]);
  const [svgStatus, setSvgStatus] = useState({});
  const [pdfStatus, setPdfStatus] = useState({ build: null, exists: false });

  // The shop is shared with SSR labels; the timezone check is client-only, so
  // via een effect zodat SSR en eerste client-render identiek blijven.
  const [legoShop, setLegoShop] = useState({ eu: false, locale: null });
  useEffect(() => {
    setLegoShop({ eu: isEuVisitor(), locale: catalogShop.toLowerCase() });
  }, [catalogShop]);

  // Prijs/beschikbaarheid bij LEGO (gecachet op onze backend). De koopknop
  // verschijnt alleen als LEGO de set aantoonbaar nog verkoopt; bij twijfel
  // (fout, rate-limit, onbekend product) blijft hij weg — de meeste sets op
  // de site zijn ouder en niet meer leverbaar.
  const [legoPrice, setLegoPrice] = useState(null);
  useEffect(() => {
    if (!legoShop.eu || !legoShop.locale || !id) return;
    if (bricklinkDesignerProgram[id]) return;
    let cancelled = false;
    api.fetchLegoPrice(id, legoShop.locale)
      .then((price) => { if (!cancelled) setLegoPrice(price); })
      .catch(() => { if (!cancelled) setLegoPrice({ available: false, error: true }); });
    return () => { cancelled = true; };
  }, [legoShop, id]);
  const legoBuyable = Boolean(legoPrice?.available && legoPrice.buyable);
  const legoOnSale = Boolean(legoPrice?.available && legoPrice.salePercentage > 0
    && legoPrice.listCentAmount && legoPrice.centAmount < legoPrice.listCentAmount);

  // Begin met één relevante zak. Dat houdt de eerste render compact en voorkomt
  // dat honderden afbeeldingen tegelijk de pagina langer maken tijdens laden.
  const [openItems, setOpenItems] = useState(() => {
    if (!hasInitialSet) return [];
    return getInitialOpenBag(initialSet.bags);
  });
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [activeBagInView, setActiveBagInView] = useState(null);
  const [isQuickJumpSticky, setIsQuickJumpSticky] = useState(false);
  const [selectedBagRange, setSelectedBagRange] = useState(0);
  // Mobiel: quick-jump standaard ingeklapt tot één regel - het volledige
  // paneel (ranges + zak-knoppen) at anders ruim een kwart van het scherm op
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [quickJumpOpen, setQuickJumpOpen] = useState(false);
  const contentRef = useRef(null);
  const quickJumpRef = useRef(null);

  const [nextHidden, setNextHidden] = useState(null);
  const [makingVisible, setMakingVisible] = useState(false);
  const [legoPieceCount, setLegoPieceCount] = useState(null);

  const [showOnlyBagsWithTempIds, setShowOnlyBagsWithTempIds] = useState(false);
  const [showBag0, setShowBag0] = useState(false);
  const [showOnlyDecoratedParts, setShowOnlyDecoratedParts] = useState(false);
  const [showOnlyMissingImages, setShowOnlyMissingImages] = useState(false);
  const [showOnlyMultiItemParts, setShowOnlyMultiItemParts] = useState(false);
  const [multiItemParts, setMultiItemParts] = useState({});
  const [failedImages, setFailedImages] = useState({});

  const [loadingBagIndex, setLoadingBagIndex] = useState(0);
  const [bagLoadedImages, setBagLoadedImages] = useState({});

  const [displayNumbersModalOpen, setDisplayNumbersModalOpen] = useState(false);
  const [editingBag, setEditingBag] = useState(null);
  const [displayNumbersInput, setDisplayNumbersInput] = useState('');
  // Onderdeel verplaatsen naar een andere bag: {part, toBagId, quantity}
  const [moveModal, setMoveModal] = useState(null);
  const [moveSaving, setMoveSaving] = useState(false);
  const [setConfirmAction, setSetConfirmAction] = useState(null);
  const [buildNameModalOpen, setBuildNameModalOpen] = useState(false);
  const [buildNameInput, setBuildNameInput] = useState('');

  const [editingNote, setEditingNote] = useState(false);
  const [noteInput, setNoteInput] = useState('');

  // Multi-build (3-in-1): de actieve build komt uit de server-response
  // (active_build), zodat admin-refetches - die altijd de complete set halen -
  // de switcher nooit uit sync brengen
  const [buildSwitching, setBuildSwitching] = useState(false);
  const buildCacheRef = useRef(new Map());
  // Admin-fotokiezer: LEGO-marketingfoto's als build-preview kiezen
  const [buildImagePickerOpen, setBuildImagePickerOpen] = useState(false);
  const [buildImageCandidates, setBuildImageCandidates] = useState(null);


  // User-account (optioneel): favorieten/collectie-knoppen
  const { account, collectionIds, toggleCollection } = useUser();
  const accountId = account?.id;

  // Sorteervoortgang leeft in de gedeelde hook (zelfde state als Sort mode);
  // de overzichtspagina toont er alleen de "Continue sorting"-badge mee en
  // laat de hook op de achtergrond de account-sync doen. active: false -
  // viermomenten (confetti) horen bij Sort mode zelf.
  const { totalCheckProgress } = useSortProgress(id, set, { active: false });

  // Migratie: wie de oude in-page Check Mode nog aan had staan gaat eenmalig
  // door naar de sort mode; de key vervalt daarna (de modus is nu de route)
  useEffect(() => {
    if (id && localStorage.getItem(`bricksperbag_checkmode_${id}`) === 'true') {
      localStorage.removeItem(`bricksperbag_checkmode_${id}`);
      router.replace(`/set/${id}/sort`);
    }
  }, [id, router]);

  // ?check=1 (oude homepage-links en bookmarks) → de sort-mode-route
  useEffect(() => {
    if (id && searchParams.get('check') === '1') {
      router.replace(`/set/${id}/sort`);
    }
  }, [id, searchParams, router]);

  // Zoeken binnen de set: vrij zoeken op naam/ID + filteren op kleur.
  // Resultaten tonen per element in wélke zak(ken) het zit.
  const [partSearch, setPartSearch] = useState('');
  const [partOrder, changePartOrder] = usePartOrder('set');
  const [selectedColors, setSelectedColors] = useState([]);
  // Kleurchips pas tonen als de zoeker "actief" is (focus of inhoud) - twee
  // rijen chips permanent in beeld reserveert te veel ruimte voor iedereen
  // die niet zoekt
  const [partSearchFocused, setPartSearchFocused] = useState(false);
  const partFilterActive = partSearch.trim().length > 0 || selectedColors.length > 0;

  const colorOptions = useMemo(() => {
    if (!set?.bags) return [];
    const byColor = new Map();
    set.bags.forEach(bag => {
      if (bag.bagNumber === '0') return;
      (bag.parts || []).forEach(p => {
        if (!p.colorName) return;
        const cur = byColor.get(p.colorName) || { name: p.colorName, hex: p.colorHex, pieces: 0 };
        cur.pieces += p.quantity || 0;
        if (!cur.hex && p.colorHex) cur.hex = p.colorHex;
        byColor.set(p.colorName, cur);
      });
    });
    // Spectrale volgorde (neutrals → regenboog) i.p.v. op aantal: het oog
    // zoekt kleuren ruimtelijk, niet op frequentie
    return sortColorOptions([...byColor.values()]);
  }, [set]);

  const searchResults = useMemo(() => {
    if (!partFilterActive || !set?.bags) return [];
    const tokens = tokenizePartQuery(partSearch);
    const colorSet = new Set(selectedColors);
    const seen = new Map();
    set.bags.forEach(bag => {
      if (bag.bagNumber === '0') return;
      (bag.parts || []).forEach(p => {
        if (colorSet.size > 0 && !colorSet.has(p.colorName)) return;
        if (tokens.length > 0 && !partMatchesTokens(buildPartHaystack(p), tokens)) return;
        const key = `${p.partId}_${p.color_id || 'nocolor'}`;
        if (!seen.has(key)) seen.set(key, { key, part: p, total: 0 });
        seen.get(key).total += p.quantity || 0;
      });
    });
    return [...seen.values()].sort((a, b) => b.total - a.total);
  }, [partFilterActive, partSearch, selectedColors, set]);

  // Vindplaatsen per element (partId+kleur → lijst van zakken met aantal):
  // voedt de "In this set"-badges in de part-detail sheet en de zoekresultaten
  const partLocationsMap = useMemo(() => {
    if (!set?.bags) return {};
    const map = {};
    set.bags.forEach(bag => {
      if (bag.bagNumber === '0') return;
      const label = bag.displayNumbers && bag.displayNumbers.length > 0
        ? bag.displayNumbers.join('+')
        : (bag.bagNumber === 'Extra parts' ? 'Extra' : bag.bagNumber);
      const chipLabel = isNaN(parseInt(label, 10)) ? label : `${bag.virtual ? 'Group' : 'Bag'} ${label}`;
      (bag.parts || []).forEach(part => {
        const key = `${part.partId}_${part.color_id || 'nocolor'}`;
        if (!map[key]) map[key] = [];
        map[key].push({ bagNumber: bag.bagNumber, chipLabel, quantity: part.quantity || 0 });
      });
    });
    return map;
  }, [set]);

  // Spring naar een zak vanuit de detail-sheet of een zoekresultaat: open de
  // accordion indien nodig en scroll met dezelfde offset als de quick-jump.
  // Met een partKey licht het onderdeel daar even op, zodat je oog het
  // meteen vindt tussen de tientallen tegels.
  // Meervoudig: een Find My Bag-deeplink kan meerdere onderdelen tegelijk
  // laten oplichten; in-set-navigatie geeft er gewoon één door
  const [highlightedPartKeys, setHighlightedPartKeys] = useState([]);
  const highlightTimerRef = useRef(null);
  const highlightStartRef = useRef(null);
  const flashHighlight = useCallback((keys) => {
    // Even leegmaken zodat een tweede klik op hetzelfde onderdeel de
    // CSS-animatie opnieuw start (zelfde className = geen replay)
    clearTimeout(highlightTimerRef.current);
    clearTimeout(highlightStartRef.current);
    setHighlightedPartKeys([]);
    highlightStartRef.current = setTimeout(() => setHighlightedPartKeys(keys), 30);
    highlightTimerRef.current = setTimeout(() => setHighlightedPartKeys([]), 3600);
  }, []);
  const jumpToBag = useCallback((bagNumber, partKey = null) => {
    const bagValue = `bag-${bagNumber}`;
    const wasOpen = openItems.includes(bagValue);
    if (!wasOpen) setOpenItems(prev => (prev.includes(bagValue) ? prev : [...prev, bagValue]));
    const keys = partKey ? (Array.isArray(partKey) ? partKey : [partKey]) : [];
    if (keys.length) flashHighlight(keys);
    const doScroll = (smooth) => {
      const element = document.querySelector(`[data-bag-id="${bagValue}"]`);
      if (!element) return;
      const quickJumpHeight = quickJumpRef.current?.offsetHeight || 0;
      const totalOffset = 70 + quickJumpHeight + 10;
      // Scroll naar de (eerste) tegel zelf als die al gerenderd is (staggered
      // loading kan achterlopen - dan is de zak-top de beste benadering)
      let target = element;
      let extra = 0;
      if (keys.length) {
        const tile = element.querySelector(`[data-part-key="${keys[0]}"]`);
        if (tile) { target = tile; extra = 90; }
      }
      const top = target.getBoundingClientRect().top + window.pageYOffset - totalOffset - extra;
      window.scrollTo({ top: Math.max(0, top), behavior: smooth ? 'smooth' : 'instant' });
    };
    if (!wasOpen) {
      setTimeout(() => doScroll(false), 50);
      setTimeout(() => doScroll(true), 400);
    } else {
      doScroll(true);
    }
  }, [openItems, flashHighlight]);
  useEffect(() => () => { clearTimeout(highlightTimerRef.current); clearTimeout(highlightStartRef.current); }, []);

  // Deep-link vanaf Find My Bag: ?bag=<bagNumber>&parts=<id,id,...> springt
  // naar de gemátchte zak (waar alle gezochte onderdelen samen zitten) en
  // licht ze allemaal op. ?part=<id> (oude vorm) blijft werken. Params worden
  // daarna gestript zodat een refresh/share niet opnieuw springt.
  const partParamHandled = useRef(false);
  useEffect(() => {
    const partsParam = searchParams.get('parts') || searchParams.get('part');
    if (!partsParam || !set || partParamHandled.current) return;
    partParamHandled.current = true;
    const bagParam = searchParams.get('bag');
    const partIds = partsParam.split(',').map(v => v.trim()).filter(Boolean);
    // Element-IDs coderen de kleur al, dus de eerste key-match per partId
    // is het juiste onderdeel
    const keys = partIds
      .map(pid => Object.keys(partLocationsMap).find(k => k.startsWith(`${pid}_`)))
      .filter(Boolean);
    // Doelzak: de meegegeven (gematchte) zak als die bestaat; anders de
    // eerste vindplaats van het eerste onderdeel
    let bagNumber = null;
    if (bagParam && set.bags?.some(b => b.bagNumber === bagParam)) bagNumber = bagParam;
    else if (keys.length) bagNumber = partLocationsMap[keys[0]][0].bagNumber;
    const params = new URLSearchParams(searchParams.toString());
    params.delete('parts'); params.delete('part'); params.delete('bag');
    const qs = params.toString();
    router.replace(`/set/${id}${qs ? `?${qs}` : ''}`, { scroll: false });
    // Even wachten tot de accordion gerenderd is voordat we springen
    if (bagNumber && keys.length) {
      setTimeout(() => jumpToBag(bagNumber, keys), 400);
      // Bijstuurlus: de gefaseerde image-loading laat zakken bóven het doel
      // nog groeien ná de eerste scroll, waardoor de tegel uit beeld schuift.
      // Volg de eerste tegel tot de layout stabiel is; duurde dat lang, vuur
      // de highlight dan opnieuw af zodat de puls niet ongezien voorbij is.
      let lastTop = null; let stable = 0; let ticks = 0;
      const iv = setInterval(() => {
        ticks += 1;
        // Binnen de dóelzak zoeken: hetzelfde element kan ook in een andere
        // zak zitten en dan trekt de lus naar de verkeerde instantie
        const el = document.querySelector(`[data-bag-id="bag-${bagNumber}"] [data-part-key="${keys[0]}"]`);
        if (el) {
          const top = el.getBoundingClientRect().top;
          const inView = top > 70 && top < window.innerHeight - 140;
          if (!inView) el.scrollIntoView({ block: 'center' });
          if (lastTop !== null && Math.abs(top - lastTop) < 1 && inView) stable += 1; else stable = 0;
          lastTop = top;
          if (stable >= 2) {
            clearInterval(iv);
            if (ticks > 4) flashHighlight(keys);
          }
        }
        if (ticks > 14) clearInterval(iv);
      }, 600);
    }
  }, [set, searchParams, partLocationsMap, jumpToBag, flashHighlight, id, router]);

  const handleImageError = useCallback((partKey) => {
    setFailedImages(prev => {
      if (prev[partKey]) return prev;
      return { ...prev, [partKey]: true };
    });
  }, []);

  const handleEditDisplayNumbers = (bag) => {
    setEditingBag(bag);
    setDisplayNumbersInput(bag.displayNumbers ? bag.displayNumbers.join(', ') : bag.bagNumber);
    setDisplayNumbersModalOpen(true);
  };

  const handleSaveDisplayNumbers = async () => {
    if (!editingBag) return;
    try {
      const cleanedInput = displayNumbersInput.split(',').map(n => n.trim()).filter(n => n).join(',');
      await api.updateBagDisplayNumbers(editingBag.id, cleanedInput || null);
      setDisplayNumbersModalOpen(false);
      window.location.reload();
    } catch (error) {
      console.error('Error updating display numbers:', error);
    }
  };

  const shouldShowBag = useCallback((bag) => {
    if (bag.bagNumber === '0') { return adminMode && showBag0; }
    if (bag.bagNumber === 'Extra parts' && (!bag.parts || bag.parts.length === 0)) return false;
    if (adminMode && showOnlyBagsWithTempIds) {
      const hasTempIds = bag.parts?.some(part => part.partId.startsWith('0_'));
      if (!hasTempIds) return false;
    }
    if (adminMode && (showOnlyDecoratedParts || showOnlyMissingImages || showOnlyMultiItemParts)) {
      const hasMatchingParts = bag.parts?.some(part => {
        const partKey = `${bag.id}_${part.partId}_${part.color_id || 'nocolor'}`;
        const isDecorated = showOnlyDecoratedParts && isDecoratedPart(part.part_ex_id);
        const hasFailedImage = showOnlyMissingImages && failedImages && failedImages[partKey];
        const isMultiItem = showOnlyMultiItemParts && multiItemParts[part.partId];
        return isDecorated || hasFailedImage || isMultiItem;
      });
      if (!hasMatchingParts) return false;
    }
    return true;
  }, [adminMode, showBag0, showOnlyBagsWithTempIds, showOnlyDecoratedParts, showOnlyMissingImages, showOnlyMultiItemParts, failedImages, multiItemParts]);

  useEffect(() => {
    if (waitingForAdminAuth) return;
    const loadSet = async () => {
      try {
        setError(null);
        // The server already rendered the set for regular visitors; only
        // refetch in admin mode (auth + hidden sets) or when SSR data is missing
        const needsFetch = adminMode || !hasInitialSet || set?.catalog_shop !== catalogShop;
        if (needsFetch) setLoading(true);
        const data = needsFetch ? await api.fetchSetById(id, adminMode, null, catalogShop) : null;
        if (needsFetch) setSet(data);
        if (adminMode) {
          const [unassignedParts, svgStatusData, multiData] = await Promise.all([
            api.getUnassignedParts(id),
            api.checkSvgParts(id),
            api.fetchMultiItemParts(id).catch(e => { console.error('Failed to load multi-item parts:', e); return {}; }),
          ]);
          setExtraParts(unassignedParts);
          setSvgStatus(svgStatusData);
          setMultiItemParts(multiData);
        }
      } catch (err) {
        setError(err);
        // Een echte 404 is verwacht gedrag, geen fout om te loggen
        if (err.kind !== ERROR_KINDS.NOT_FOUND) console.error(err);
      } finally {
        setLoading(false);
      }
    };
    loadSet();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, adminMode, waitingForAdminAuth, reloadKey, catalogShop]);

  useEffect(() => {
    if (!adminMode || !id) return;
    api.getNextHiddenSet(id).then(data => setNextHidden(data)).catch(() => setNextHidden(null));
    api.getLegoPieceCount(id).then(data => setLegoPieceCount(data.pieceCount)).catch(() => setLegoPieceCount(null));
  }, [id, adminMode]);

  useEffect(() => {
    if (!set?.bags || set.bags.length === 0) return;
    const visibleBags = set.bags.filter(bag => {
      if (bag.bagNumber === '0') return adminMode && showBag0;
      if (bag.bagNumber === 'Extra parts' && (!bag.parts || bag.parts.length === 0)) return false;
      return true;
    });
    const bagNumbers = visibleBags.map(bag => `bag-${bag.bagNumber}`);
    setOpenItems((previous) => {
      // Admin-overzichten houden alle groepen open; voor bezoekers kiezen we
      // de URL-zak, de laatst geopende zak of anders de eerste zichtbare zak.
      if (adminMode) return bagNumbers;
      const urlBag = searchParams.get('bag');
      const urlValue = urlBag ? `bag-${urlBag}` : null;
      const storageKey = `bricksperbag_last_bag_${id}_${set.active_build || 'complete'}`;
      let storedValue = null;
      try { storedValue = localStorage.getItem(storageKey); } catch { /* storage geblokkeerd */ }
      const preferred = [urlValue, storedValue, ...previous, bagNumbers[0]]
        .find((value) => value && bagNumbers.includes(value));
      return preferred ? [preferred] : [];
    });
  }, [set?.bags, set?.active_build, adminMode, showBag0, id, searchParams]);

  const previousOpenItemsRef = useRef(openItems);
  const previousBagStorageKeyRef = useRef(null);
  useEffect(() => {
    const storageKey = `bricksperbag_last_bag_${id}_${set?.active_build || 'complete'}`;
    if (previousBagStorageKeyRef.current !== storageKey) {
      previousBagStorageKeyRef.current = storageKey;
      previousOpenItemsRef.current = [];
    }
    if (!adminMode && openItems.length > 0) {
      const newlyOpened = openItems.find((value) => !previousOpenItemsRef.current.includes(value));
      const activeValue = newlyOpened || openItems[openItems.length - 1];
      try { localStorage.setItem(storageKey, activeValue); } catch { /* storage geblokkeerd */ }
    }
    previousOpenItemsRef.current = openItems;
  }, [openItems, adminMode, id, set?.active_build]);

  const visibleBagsForLoading = useMemo(() => {
    if (!set?.bags) return [];
    return set.bags.filter(bag => {
      if (bag.bagNumber === '0') return adminMode && showBag0;
      if (bag.bagNumber === 'Extra parts' && (!bag.parts || bag.parts.length === 0)) return false;
      return true;
    });
  }, [set?.bags, adminMode, showBag0]);

  const handleBagImageLoad = useCallback((bagNumber, loaded, total) => {
    setBagLoadedImages(prev => {
      const cur = prev[bagNumber];
      if (cur && cur.loaded === loaded && cur.total === total) return prev;
      return { ...prev, [bagNumber]: { loaded, total } };
    });
    if (loaded >= total && visibleBagsForLoading.length > 0) {
      const currentBagIdx = visibleBagsForLoading.findIndex(b => b.bagNumber === bagNumber);
      if (currentBagIdx >= 0) {
        // Defer the advance out of the effect flush: with a warm image cache
        // all load-events and bag-completions land in one synchronous burst,
        // and back-to-back commits in that burst trip React's
        // 50-nested-update heuristic (error #185)
        setTimeout(() => {
          setLoadingBagIndex(prev => (currentBagIdx === prev ? prev + 1 : prev));
        }, 0);
      }
    }
  }, [visibleBagsForLoading]);

  const shouldBagLoad = useCallback((bagNumber) => {
    if (visibleBagsForLoading.length === 0) return false;
    const bagIdx = visibleBagsForLoading.findIndex(b => b.bagNumber === bagNumber);
    return bagIdx >= 0 && bagIdx <= loadingBagIndex;
  }, [visibleBagsForLoading, loadingBagIndex]);

  const isBagLoaded = useCallback((bagNumber) => {
    const bagState = bagLoadedImages[bagNumber];
    if (!bagState) return false;
    return bagState.loaded >= bagState.total;
  }, [bagLoadedImages]);

  const activeBuild = set?.active_build || null;
  const pdfExists = pdfStatus.build === activeBuild && pdfStatus.exists;

  useEffect(() => {
    let active = true;
    api.checkPdfExists(id, activeBuild).then(result => {
      if (active) setPdfStatus({ build: activeBuild, exists: result.exists });
    });
    return () => { active = false; };
  }, [id, activeBuild]);

  const switchBuild = useCallback(async (value) => {
    const target = value === 'complete' ? null : value;
    if ((set?.active_build || null) === target) return;
    // Huidige weergave cachen zodat terugschakelen instant is (niet in admin-
    // mode: daar muteren edits de data en zou de cache staal worden)
    if (!adminMode && set) buildCacheRef.current.set(set.active_build || 'complete', set);
    setBuildSwitching(true);
    try {
      const cacheKey = target || 'complete';
      let data = adminMode ? null : buildCacheRef.current.get(cacheKey);
      if (!data) {
        data = await api.fetchSetById(id, adminMode, target, catalogShop);
        if (!adminMode) buildCacheRef.current.set(cacheKey, data);
      }
      // Staggered image-loading opnieuw laten beginnen voor de nieuwe bag-lijst
      setLoadingBagIndex(0);
      setBagLoadedImages({});
      setSet(data);
      const params = new URLSearchParams(searchParams.toString());
      if (target) params.set('build', target); else params.delete('build');
      const qs = params.toString();
      router.replace(`/set/${id}${qs ? `?${qs}` : ''}`, { scroll: false });
    } catch (err) {
      console.error('Error switching build:', err);
      notifyError(err, { title: 'Failed to load build' });
    } finally {
      setBuildSwitching(false);
    }
  }, [id, adminMode, set, searchParams, router]);

  // Deeplink: /set/123?build=sm02 opent direct in die build-weergave
  const buildDeeplinkDone = useRef(false);
  useEffect(() => {
    if (buildDeeplinkDone.current || !set) return;
    buildDeeplinkDone.current = true;
    const urlBuild = searchParams.get('build');
    if (urlBuild && (set.active_build || null) !== urlBuild
        && set.builds?.some(b => b.code === urlBuild)) {
      switchBuild(urlBuild);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [set]);

  const updateExtraParts = async () => {
    if (adminMode) {
      try {
        const unassignedParts = await api.getUnassignedParts(id);
        setExtraParts(unassignedParts);
      } catch (error) { console.error('Error reloading unassigned parts:', error); }
    }
  };

  // Onderdeel naar een andere bag verplaatsen (opent het keuzevenster)
  const handleMovePart = useCallback((part) => {
    setMoveModal({ part, toBagId: null, quantity: part.quantity });
  }, []);

  const handleDisconnectPart = useCallback(async (bagId, partId, colorId) => {
    try {
      await api.disconnectPartFromBag(id, bagId, partId, colorId);
      const data = await api.fetchSetById(id, adminMode, null, catalogShop);
      setSet(data);
      await updateExtraParts();
    } catch (error) { console.error('Error disconnecting part:', error); notifyError(error, { title: 'Failed to disconnect part' }); }
  }, [id, adminMode]);

  const handleDeletePart = useCallback(async (bagId, partId, colorId) => {
    try {
      await api.deletePartFromBag(id, bagId, partId, colorId);
      const data = await api.fetchSetById(id, adminMode, null, catalogShop);
      setSet(data);
      await updateExtraParts();
    } catch (error) { console.error('Error deleting part:', error); notifyError(error, { title: 'Failed to delete part' }); }
  }, [id, adminMode]);

  const handleUpdatePartId = useCallback(async (bagId, oldPartId, colorId, newPartId, quantity) => {
    try {
      await api.updatePartId(id, bagId, oldPartId, colorId, newPartId, quantity);
      const data = await api.fetchSetById(id, adminMode, null, catalogShop);
      setSet(data);
      await updateExtraParts();
    } catch (error) { console.error('Error updating part:', error); notifyError(error, { title: 'Failed to update part' }); }
  }, [id, adminMode]);

  const handleUpdateQuantity = useCallback(async (bagId, partId, colorId, quantity) => {
    try {
      await api.updatePartQuantity(id, bagId, partId, colorId, quantity);
      const data = await api.fetchSetById(id, adminMode, null, catalogShop);
      setSet(data);
    } catch (error) { console.error('Error updating quantity:', error); notifyError(error, { title: 'Failed to update quantity' }); }
  }, [id, adminMode]);

  const handleAddPart = useCallback(async (bagId, partId, quantity) => {
    try {
      await api.addPartToBag(id, bagId, partId, quantity);
      const data = await api.fetchSetById(id, adminMode, null, catalogShop);
      setSet(data);
      await updateExtraParts();
    } catch (error) { console.error('Error adding part:', error); throw error; }
  }, [id, adminMode]);

  const handleDeleteBag = useCallback((bagId, bagNumber) => {
    setSetConfirmAction({ type: 'delete-bag', bagId, bagNumber });
  }, []);

  const confirmDeleteBag = useCallback(async (bagId) => {
    try {
      await api.deleteBag(id, bagId);
      const data = await api.fetchSetById(id, adminMode, null, catalogShop);
      setSet(data);
      await updateExtraParts();
    } catch (error) { console.error('Error deleting bag:', error); notifyError(error, { title: 'Failed to delete bag' }); }
  }, [id, adminMode]);

  const handleMakeVisibleAndNext = useCallback(async () => {
    try {
      setMakingVisible(true);
      await api.toggleSetVisibility(id);
      if (nextHidden?.nextId) {
        setShowOnlyDecoratedParts(false);
        setShowOnlyMissingImages(false);
        setShowOnlyMultiItemParts(false);
        router.push(`/set/${nextHidden.nextId}?admin=true`);
      } else {
        const data = await api.fetchSetById(id, adminMode, null, catalogShop);
        setSet(data);
        setNextHidden(null);
      }
    } catch (error) { console.error('Error making set visible:', error); notifyError(error, { title: 'Failed to make set visible' }); }
    finally { setMakingVisible(false); }
  }, [id, adminMode, nextHidden, router]);

  const handleUploadPreviewImage = useCallback(async (bagId, file) => {
    try {
      await api.uploadBagPreviewImage(bagId, file);
      const data = await api.fetchSetById(id, adminMode, null, catalogShop);
      setSet(data);
    } catch (error) { console.error('Error uploading preview image:', error); notifyError(error, { title: 'Failed to upload preview image' }); }
  }, [id, adminMode]);

  const handleDeletePreviewImage = useCallback((bagId) => {
    setSetConfirmAction({ type: 'delete-preview', bagId });
  }, []);

  const confirmDeletePreviewImage = useCallback(async (bagId) => {
    try {
      await api.deleteBagPreviewImage(bagId);
      const data = await api.fetchSetById(id, adminMode, null, catalogShop);
      setSet(data);
    } catch (error) { console.error('Error deleting preview image:', error); notifyError(error, { title: 'Failed to delete preview image' }); }
  }, [id, adminMode]);

  const handleDownloadPdf = async () => {
    if (!set) return;
    trackEvent('Set Downloaded', { set: String(id), format: 'pdf', scope: 'set' });
    if (pdfExists) {
      const downloadUrl = api.getPdfDownloadUrl(id, activeBuild);
      window.open(downloadUrl, '_blank');
      return;
    }
    setIsGeneratingPdf(true);
    try {
      // Load PDF libraries on demand - they're too heavy for the page bundle
      const [{ jsPDF }, { default: html2canvas }] = await Promise.all([
        import('jspdf'),
        import('html2canvas'),
      ]);
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 15;

      pdf.setFontSize(40);
      pdf.setFont('helvetica', 'bold');
      pdf.text('BRICKSPERBAG.COM', pageWidth / 2, 40, { align: 'center' });

      if (set.image_url) {
        try {
          const setImageUrl = getImageUrl(set.image_url);
          const imgElement = new window.Image();
          imgElement.crossOrigin = 'anonymous';
          await new Promise((resolve, reject) => {
            imgElement.onload = resolve;
            imgElement.onerror = reject;
            imgElement.src = setImageUrl;
          });
          const imgWidth = 120;
          const imgHeight = (imgElement.height / imgElement.width) * imgWidth;
          pdf.addImage(imgElement, 'PNG', (pageWidth - imgWidth) / 2, 60, imgWidth, imgHeight);
        } catch (error) { console.error('Error loading set image:', error); }
      }

      pdf.setFontSize(24);
      pdf.setFont('helvetica', 'bold');
      pdf.text(set.name || 'Unnamed Set', pageWidth / 2, 140, { align: 'center' });
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Set Number: ${set.id}`, pageWidth / 2, 155, { align: 'center' });
      if (set.theme) { pdf.text(`Theme: ${set.theme}${set.year ? ` (${set.year})` : ''}`, pageWidth / 2, 165, { align: 'center' }); }
      const totalBags = set.bag_count || set.bags?.filter(bag => bag.bagNumber !== 'Extra parts' && bag.bagNumber !== 'Extra' && bag.bagNumber !== '0').length || 0;
      pdf.text(`Total Bags: ${totalBags}`, pageWidth / 2, 175, { align: 'center' });
      pdf.text(`Total Pieces: ${totalPieces}`, pageWidth / 2, 185, { align: 'center' });
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'italic');
      pdf.text('Generated by BRICKSPERBAG.COM', pageWidth / 2, pageHeight - 20, { align: 'center' });

      const bags = set.bags?.filter(bag => {
        if (bag.bagNumber === '0') return false;
        if (bag.bagNumber === 'Extra parts' && (!bag.parts || bag.parts.length === 0)) return false;
        return true;
      }) || [];

      for (let i = 0; i < bags.length; i++) {
        const bag = bags[i];
        pdf.addPage();
        pdf.setFontSize(28);
        pdf.setFont('helvetica', 'bold');
        pdf.text(`Bag ${bag.bagNumber}`, pageWidth / 2, margin + 10, { align: 'center' });

        const bagElement = document.querySelector(`[data-bag-id="bag-${bag.bagNumber}"]`);
        if (bagElement) {
          try {
            const bagValue = `bag-${bag.bagNumber}`;
            if (!openItems.includes(bagValue)) {
              setOpenItems([...openItems, bagValue]);
              await new Promise(resolve => setTimeout(resolve, 300));
            }
            const panelElement = bagElement.querySelector('.mantine-Accordion-panel');
            if (panelElement) {
              const canvas = await html2canvas(panelElement, { scale: 2, logging: false, useCORS: true, backgroundColor: '#f8f9fa' });
              const imgData = canvas.toDataURL('image/png');
              const horizontalMargin = 8 * 0.264583;
              const imgWidth = pageWidth - (2 * horizontalMargin);
              const imgHeight = (canvas.height / canvas.width) * imgWidth;
              const topPosition = margin + 25;
              const maxHeight = pageHeight - topPosition - 15;
              let finalHeight = imgHeight;
              let finalWidth = imgWidth;
              if (imgHeight > maxHeight) {
                finalHeight = maxHeight;
                finalWidth = (canvas.width / canvas.height) * maxHeight;
                const xPosition = (pageWidth - finalWidth) / 2;
                pdf.addImage(imgData, 'PNG', xPosition, topPosition, finalWidth, finalHeight);
              } else {
                pdf.addImage(imgData, 'PNG', horizontalMargin, topPosition, finalWidth, finalHeight);
              }
            }
          } catch (error) {
            console.error(`Error rendering bag ${bag.bagNumber}:`, error);
            pdf.setFontSize(12);
            pdf.text(`Error rendering bag content`, pageWidth / 2, 100, { align: 'center' });
          }
        }
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        pdf.text(`BRICKSPERBAG.COM`, margin, pageHeight - 10);
        pdf.text(`Page ${i + 2} of ${bags.length + 1}`, pageWidth - margin, pageHeight - 10, { align: 'right' });
      }
      pdf.save(`bricksperbag-set-${set.id}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      notifyError(error, { title: 'Failed to generate PDF' });
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  useEffect(() => {
    if (!set || !set.bags) return;
    const bags = set.bags.filter(bag => {
      if (bag.bagNumber === '0') return false;
      if (bag.bagNumber === 'Extra parts' && (!bag.parts || bag.parts.length === 0)) return false;
      return true;
    });
    const headerHeight = 70;
    const quickJumpHeight = quickJumpRef.current?.offsetHeight || 0;
    const totalOffset = headerHeight + quickJumpHeight + 10;
    const observerOptions = { root: null, rootMargin: `-${totalOffset}px 0px -50% 0px`, threshold: 0 };
    const visibleBags = new Map();
    let debounceTimer = null;
    const updateActiveBag = () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        if (visibleBags.size > 0) {
          const closest = Array.from(visibleBags.entries()).reduce((prev, curr) =>
            Math.abs(curr[1]) < Math.abs(prev[1]) ? curr : prev
          );
          setActiveBagInView(closest[0]);
        }
      }, 100);
    };
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const bagId = entry.target.getAttribute('data-bag-id');
        if (entry.isIntersecting) {
          visibleBags.set(bagId, entry.target.getBoundingClientRect().top);
        } else {
          visibleBags.delete(bagId);
        }
      });
      updateActiveBag();
    }, observerOptions);
    bags.forEach(bag => {
      const bagValue = `bag-${bag.bagNumber}`;
      const element = document.querySelector(`[data-bag-id="${bagValue}"]`);
      if (element) { observer.observe(element); }
    });
    return () => { observer.disconnect(); };
  }, [set]);

  const debouncedHandleScroll = useDebouncedCallback(() => {
    if (quickJumpRef.current) {
      const rect = quickJumpRef.current.getBoundingClientRect();
      setIsQuickJumpSticky(rect.top <= 70);
    }
  }, 16);

  useEffect(() => {
    window.addEventListener('scroll', debouncedHandleScroll, { passive: true });
    debouncedHandleScroll();
    return () => window.removeEventListener('scroll', debouncedHandleScroll);
  }, [set, debouncedHandleScroll]);

  useEffect(() => {
    if (activeBagInView && set?.bags) {
      const activeBagNumber = parseInt(activeBagInView.replace('bag-', ''));
      if (!isNaN(activeBagNumber)) {
        const rangeSize = 10;
        const newRangeIndex = Math.floor((activeBagNumber - 1) / rangeSize);
        if (newRangeIndex !== selectedBagRange) {
          setSelectedBagRange(newRangeIndex);
        }
      }
    }
  }, [activeBagInView, set?.bags]);

  // Full-inventory aggregates, memoized on `set` - these used to run inline on
  // every render (i.e. on every check-mode tap and scroll-driven state change).
  // NOTE: hooks must stay above the early returns below.
  const { totalPieces, uniqueParts, allBagsVirtual } = useMemo(() => {
    if (!set?.bags) return { totalPieces: 0, uniqueParts: 0, allBagsVirtual: false };
    // In build-weergave telt de "Left over"-group niet mee: de chips tonen wat
    // deze build nodig heeft
    const countedBags = set.bags.filter(bag => bag.bagNumber !== '0' && !bag.leftover);
    const totalPieces = countedBags.reduce((total, bag) =>
      total + (bag.parts || [])
        .filter(part => !part.partId.startsWith('0_'))
        .reduce((bagTotal, part) => bagTotal + part.quantity, 0), 0
    );
    const uniqueParts = new Set(
      countedBags.flatMap(bag => (bag.parts || [])
        .filter(part => !part.partId.startsWith('0_'))
        .map(part => part.partId)
      )
    ).size;
    const realBags = set.bags.filter(bag => bag.bagNumber !== '0' && bag.bagNumber !== 'Extra parts' && bag.bagNumber !== 'Extra');
    const allBagsVirtual = realBags.length > 0 && realBags.every(bag => bag.virtual);
    return { totalPieces, uniqueParts, allBagsVirtual };
  }, [set]);

  // Visible bags + open-items lookup, memoized once instead of re-filtering
  // 3-4 times per render (and O(bags²) includes() lookups in the accordion map)
  const visibleBags = useMemo(
    () => (set?.bags ? set.bags.filter(shouldShowBag) : []),
    [set, shouldShowBag]
  );
  const openItemsSet = useMemo(() => new Set(openItems), [openItems]);

  if (loading || waitingForAdminAuth) {
    // Skeleton mirrors the real layout (compact hero, stat chips, bag rows) so
    // the page doesn't jump when the data arrives.
    return (
      <Container size="xl" pt="xs" pb="xl">
        <Skeleton height={26} width={90} radius="xl" mb={6} />
        <Paper p={0} radius="lg" style={{ backgroundColor: 'var(--bpb-surface)', border: '1px solid var(--bpb-border)', boxShadow: 'var(--bpb-shadow-md)', overflow: 'hidden' }}>
          <Box style={{ padding: '56px 32px 28px' }}>
            <Group gap="xl" align="center" wrap="nowrap">
              <Skeleton height={150} width={200} radius="md" style={{ flexShrink: 0, maxWidth: '38%' }} />
              <Stack gap="sm" style={{ flex: 1 }}>
                <Skeleton height={32} width="min(340px, 80%)" radius="xl" />
                <Skeleton height={30} width={170} radius="xl" />
              </Stack>
            </Group>
            <Group gap={6} mt="md">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} height={28} width={100} radius="xl" />
              ))}
            </Group>
          </Box>
          <Box p="xl" style={{ borderTop: '1px solid #f1f3f4' }}>
            <Stack gap="sm">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} height={70} radius="lg" />
              ))}
            </Stack>
          </Box>
        </Paper>
      </Container>
    );
  }

  // Echte 404 (set bestaat niet) en backendstoring krijgen elk hun eigen
  // scherm: bij een storing is retry zinvol, bij een 404 niet
  if ((error && error.kind === ERROR_KINDS.NOT_FOUND) || (!error && !set)) {
    return (
      <Container size="xl" py="xl">
        <Stack align="center" gap="md" py={60}>
          <Text fz={64} fw={800} c="blue.6" lh={1} aria-hidden>404</Text>
          <Title order={2}>Set not found</Title>
          <Text c="dimmed" ta="center" maw={420}>
            We don&apos;t have set {id} (yet). You can request it via the homepage.
          </Text>
          <Button component={Link} href="/">Browse all sets</Button>
        </Stack>
      </Container>
    );
  }

  if (error) {
    return (
      <Container size="xl" py="xl">
        <Stack align="center" gap="md" py={60} role="alert">
          <Alert icon={<IconAlertTriangle size={16} />} title="Couldn't load this set" color="red" maw={480}>
            {friendlyMessage(error)}
          </Alert>
          <Group>
            <Button leftSection={<IconRefresh size={16} />} onClick={() => setReloadKey(k => k + 1)}>
              Try again
            </Button>
            <Button component={Link} href="/" variant="light">Back to home</Button>
          </Group>
        </Stack>
      </Container>
    );
  }

  // In build-weergave toont de hero de render van dát model; zonder setlogo
  // valt de complete weergave terug op de hoofdbuild-render
  const activeBuildInfo = activeBuild ? set.builds?.find(b => b.code === activeBuild) : null;
  const setImageUrl = activeBuildInfo?.imageUrl
    ? getImageUrl(activeBuildInfo.imageUrl)
    : (set.image_url
      ? getThumbUrl(set.image_url, 800)
      : (set.main_build_image ? getImageUrl(set.main_build_image) : 'https://placehold.co/400x200?text=No+Image'));

  const bagCount = set.bag_count
    || set.bags?.filter(bag => bag.bagNumber !== 'Extra parts' && bag.bagNumber !== 'Extra' && bag.bagNumber !== '0').length
    || 0;
  const statChipStyle = {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    backgroundColor: '#fff', border: '1px solid #e9ecef', borderRadius: 999,
    padding: '4px 12px', boxShadow: '0 1px 3px rgba(16, 24, 40, 0.05)',
  };

  return (
    <Container size="xl" ref={contentRef} pt="xs" pb="xl">
      <Button
        component={Link}
        href="/"
        variant="subtle"
        color="gray"
        size="xs"
        radius="xl"
        mb={6}
        leftSection={<IconArrowLeft size={14} />}
        style={{ fontWeight: 600, color: '#66707a' }}
      >
        All sets
      </Button>
      <Paper
        p={0}
        radius="lg"
        style={{
          backgroundColor: '#fff',
          border: '1px solid #e9ecef',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.06)',
        }}
      >
        {/* Hero Section */}
        <Box className="set-hero stud-overlay" style={{
          position: 'relative',
          background: 'linear-gradient(180deg, #EAF2F8 0%, #fff 70%)',
          padding: '56px 32px 28px',
          borderRadius: '16px 16px 0 0',
        }}>
          <Group
            justify="space-between"
            align="center"
            style={{ position: 'absolute', top: 16, left: 16, right: 16, zIndex: 1 }}
          >
            <VerificationBadge verified={set.verified} source={set.source} showUnverified={true} size="md" />
            <Group gap="xs" align="center">
              <Tooltip label="Report an issue" withArrow events={{ hover: true, focus: true, touch: true }}>
                <ActionIcon
                  variant="default" radius="xl"
                  aria-label="Report an issue with this set"
                  onClick={() => router.push(`/report?set=${set.id}`)}
                  style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.9)', border: '1px solid rgba(0, 0, 0, 0.06)',
                    backdropFilter: 'blur(4px)', width: 28, height: 28, minWidth: 28,
                  }}
                >
                  <IconAlertTriangle size={14} style={{ color: '#fd7e14' }} />
                </ActionIcon>
              </Tooltip>
            </Group>
          </Group>

          <Group gap="xl" align="center" wrap="nowrap" className="set-hero-row">
            <Image
              src={setImageUrl} height={150} alt={set.name} fit="contain"
              // multiply laat de witte fotostudio-achtergrond wegvallen tegen
              // de hero-gradient (zelfde truc als de part-renders in de grid)
              style={{ flexShrink: 0, maxWidth: '38%', mixBlendMode: 'multiply' }}
              fallbackSrc="https://placehold.co/400x200?text=No+Image"
              className="set-hero-image"
            />
            <Stack gap="sm" style={{ minWidth: 0, flex: 1 }}>
              <Title order={1} className="set-title" style={{ color: '#212529', fontWeight: 700 }}>{set.name}</Title>

              <Group gap="sm">
                <FavoriteHeart setId={id} size="lg" />
                <Button
                  size="xs"
                  radius="xl"
                  variant={collectionIds.has(String(id)) ? 'light' : 'default'}
                  color={collectionIds.has(String(id)) ? 'green' : 'gray'}
                  leftSection={collectionIds.has(String(id)) ? <IconCheck size={14} /> : <IconPlus size={14} />}
                  onClick={() => {
                    if (!account) {
                      trackEvent('Collection Login Required', { source: 'set' });
                      router.push('/account/login');
                      return;
                    }
                    toggleCollection(String(id));
                  }}
                >
                  {collectionIds.has(String(id)) ? 'In my collection' : 'I own this set'}
                </Button>
                {adminMode && legoPieceCount && (
                  <Text size="xs" fw={600} style={{ color: '#228be6' }}>LEGO: {legoPieceCount}</Text>
                )}
              </Group>
            </Stack>
          </Group>

          <Group gap={6} mt="md" className="set-stat-chips">
                {activeBuild ? (
                  <Tooltip label="Groups follow the build order of this model. Sort your parts into piles group by group." withArrow position="bottom" multiline w={220} events={{ hover: true, focus: true, touch: true }}>
                    <Box style={{ ...statChipStyle, cursor: 'help' }}>
                      <IconMoneybag size={15} stroke={1.7} color="#1971c2" />
                      <Text component="span" fw={700} size="sm">{set.bags.filter(b => !b.leftover && b.bagNumber !== '0').length}</Text>
                      <Text component="span" c="dimmed" size="sm">Groups</Text>
                    </Box>
                  </Tooltip>
                ) : allBagsVirtual ? (
                  <Tooltip label="This set comes in 1 bag. We've split the parts into groups to make sorting easier." withArrow position="bottom" multiline w={220} events={{ hover: true, focus: true, touch: true }}>
                    <Box style={{ ...statChipStyle, cursor: 'help' }}>
                      <IconMoneybag size={15} stroke={1.7} color="#1971c2" />
                      <Text component="span" fw={700} size="sm">{bagCount}</Text>
                      <Text component="span" c="dimmed" size="sm">Groups</Text>
                    </Box>
                  </Tooltip>
                ) : (
                  <Box style={statChipStyle}>
                    <IconMoneybag size={15} stroke={1.7} color="#1971c2" />
                    <Text component="span" fw={700} size="sm">{bagCount}</Text>
                    <Text component="span" c="dimmed" size="sm">{bagCount === 1 ? 'Bag' : 'Bags'}</Text>
                  </Box>
                )}

                {(set.sticker_sheets || 0) > 0 ? (
                  <Tooltip label={`This set includes ${set.sticker_sheets} sticker sheet${set.sticker_sheets > 1 ? 's' : ''}`} withArrow position="bottom" events={{ hover: true, focus: true, touch: true }}>
                    <Box style={{ ...statChipStyle, cursor: 'help' }}>
                      <IconCube size={15} stroke={1.7} color="#e8590c" />
                      <Text component="span" fw={700} size="sm">
                        {totalPieces}<span style={{ color: '#868e96', fontWeight: 500 }}>+{set.sticker_sheets}</span>
                      </Text>
                      <Text component="span" c="dimmed" size="sm">Pieces</Text>
                    </Box>
                  </Tooltip>
                ) : (
                  <Box style={statChipStyle}>
                    <IconCube size={15} stroke={1.7} color="#e8590c" />
                    <Text component="span" fw={700} size="sm">{totalPieces}</Text>
                    <Text component="span" c="dimmed" size="sm">Pieces</Text>
                  </Box>
                )}

                <Box style={statChipStyle}>
                  <IconStack2 size={15} stroke={1.7} color="#099268" />
                  <Text component="span" fw={700} size="sm">{uniqueParts}</Text>
                  <Text component="span" c="dimmed" size="sm">Unique</Text>
                </Box>

                {(() => {
                  const bdpUrl = bricklinkDesignerProgram[set.id];
                  const href = bdpUrl || legoProductUrl(set.id, legoShop.locale);
                  const label = bdpUrl ? 'View on BrickLink' : 'View on LEGO.com';
                  return (
                    <Tooltip label={label} withArrow position="bottom" events={{ hover: true, focus: true, touch: true }}>
                      <Box
                        component="a" href={href} target="_blank" rel={bdpUrl ? 'noopener noreferrer' : affiliateRel}
                        onClick={bdpUrl ? undefined : () => trackEvent('LEGO Shop Click', { source: 'set-chip', set: set.id })}
                        style={{ ...statChipStyle, textDecoration: 'none', cursor: 'pointer' }}
                      >
                        <IconHash size={15} stroke={1.7} color="#6741d9" />
                        <Text component="span" fw={700} size="sm" style={{ color: '#228be6' }}>{set.id}</Text>
                      </Box>
                    </Tooltip>
                  );
                })()}

                {(set.build_count || 0) >= 2 && !(set.theme || '').toLowerCase().includes('-in-1') && (
                  <Tooltip label={`This set has ${set.build_count} different builds. Pick one below to see only the parts you need`} withArrow position="bottom" multiline w={240} events={{ hover: true, focus: true, touch: true }}>
                    <Badge
                      variant="light" size="md" radius="xl"
                      style={{
                        backgroundColor: '#fff5f5', color: '#e03131', fontWeight: 700, height: 28,
                        border: '1px solid #ffc9c9', textTransform: 'none', cursor: 'help',
                      }}
                    >
                      {set.build_style === 'shared_variants' ? `${set.build_count} variants` : `${set.build_count}-in-1`}
                    </Badge>
                  </Tooltip>
                )}
                <SetLabels labels={set.labels} shop={set.catalog_shop || catalogShop} group="regular" />
                {set.theme && (
                  <Badge
                    component="button"
                    type="button"
                    className="theme-badge-clickable"
                    variant="light" size="md" radius="xl"
                    onClick={() => router.push(`/?theme=${encodeURIComponent(set.theme)}`)}
                    style={{
                      backgroundColor: '#f8f9fa', color: '#868e96', fontWeight: 500, height: 28,
                      border: '1px solid #e9ecef', cursor: 'pointer', textTransform: 'none',
                    }}
                  >
                    {set.theme}
                  </Badge>
                )}
                {set.year && (
                  <Badge
                    component="button"
                    type="button"
                    className="theme-badge-clickable"
                    variant="light" size="md" radius="xl"
                    onClick={() => router.push(`/?search=${set.year}`)}
                    style={{
                      backgroundColor: '#f8f9fa', color: '#868e96', fontWeight: 500, height: 28,
                      border: '1px solid #e9ecef', cursor: 'pointer', textTransform: 'none',
                    }}
                  >
                    {set.year}
                  </Badge>
                )}
                {set.source === 'community' && (
                  <Tooltip label={`Verified by ${set.community_helpers} community helper${set.community_helpers !== 1 ? 's' : ''}`} withArrow events={{ hover: true, focus: true, touch: true }}>
                    <Badge
                      variant="light" size="md" radius="xl"
                      leftSection={<IconUsers size={12} />}
                      style={{
                        backgroundColor: '#e6fcf5', color: '#099268', fontWeight: 500, height: 28,
                        border: '1px solid #c3fae8', textTransform: 'none', cursor: 'help',
                      }}
                    >
                      Community{set.community_helpers > 0 ? ` · ${set.community_helpers}` : ''}
                    </Badge>
                  </Tooltip>
                )}
                <SetLabels labels={set.labels} shop={set.catalog_shop || catalogShop} group="special" />
              </Group>
        </Box>

        {/* Info Section */}
        <Box p="xl" className="set-info-section" style={{ borderTop: '1px solid #f1f3f4' }}>
          {/* Admin Note Info Bar */}
          {(set.admin_note || (adminMode && editingNote)) && (
            <Alert
              variant="light" color="yellow" radius="md" mb="xl"
              icon={<IconInfoCircle size={20} />}
              title={adminMode && !editingNote ? (
                <Group gap="xs" justify="space-between" style={{ width: '100%' }}>
                  <Text fw={600} size="sm">Note</Text>
                  <ActionIcon
                    size="xs" variant="subtle" color="yellow"
                    aria-label="Edit public set note"
                    onClick={() => { setNoteInput(set.admin_note || ''); setEditingNote(true); }}
                  >
                    <IconEdit size={14} />
                  </ActionIcon>
                </Group>
              ) : undefined}
            >
              {editingNote ? (
                <Group gap="xs" align="flex-end">
                  <TextInput
                    placeholder="Add a note for users (e.g. 'Some extra parts may belong in bag 3')"
                    value={noteInput}
                    onChange={(e) => setNoteInput(e.currentTarget.value)}
                    style={{ flex: 1 }}
                    size="sm"
                    autoFocus
                  />
                  <Button size="xs" color="yellow" variant="filled" onClick={async () => {
                    try {
                      await api.updateAdminNote(id, noteInput || null);
                      const data = await api.fetchSetById(id, adminMode, null, catalogShop);
                      setSet(data);
                      setEditingNote(false);
                    } catch (error) { console.error('Error updating note:', error); }
                  }}>Save</Button>
                  <Button size="xs" variant="default" onClick={() => setEditingNote(false)}>Cancel</Button>
                </Group>
              ) : (
                <Text size="sm">{set.admin_note}</Text>
              )}
            </Alert>
          )}

          {/* Admin: Add note button (when no note exists) */}
          {adminMode && !set.admin_note && !editingNote && (
            <Button
              variant="subtle" color="yellow" size="xs" mb="md"
              leftSection={<IconEdit size={14} />}
              onClick={() => { setNoteInput(''); setEditingNote(true); }}
            >
              Add note
            </Button>
          )}

          {/* Action Buttons */}
          <Group justify="space-between" mb="xl" className="set-action-buttons" wrap="wrap" gap="sm">
            <Group>
              <Title order={3} style={{ fontWeight: 600, color: '#212529' }}>{allBagsVirtual ? 'Groups' : 'Bags'}</Title>
              {allBagsVirtual && <Text size="sm" c="dimmed">sorted by build order</Text>}
              {adminMode && (
                <>
                  <Badge color="red" size="lg" variant="filled" radius="md">ADMIN MODE</Badge>
                  <Button
                    variant="light" color="orange" size="xs" radius="md"
                    component="a"
                    href={`https://rebrickable.com/sets/${id}-1/x/?inventory=1#parts`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Rebrickable Inventory
                  </Button>
                  {!set.visible && (
                    <Button
                      variant="filled" color="green" size="sm" radius="xl" loading={makingVisible}
                      leftSection={<IconEye size={16} />}
                      rightSection={nextHidden?.nextId ? <IconArrowRight size={16} /> : null}
                      onClick={handleMakeVisibleAndNext}
                    >
                      {nextHidden?.nextId ? `Make Visible & Next (${nextHidden.hiddenCount} hidden)` : 'Make Visible'}
                    </Button>
                  )}
                </>
              )}
            </Group>
            <Group gap="xs" wrap="nowrap">
              {legoShop.eu && legoBuyable && !bricklinkDesignerProgram[set.id] && (
                <Tooltip
                  label={affiliateEnabled
                    ? 'Affiliate link: we may earn a commission, at no extra cost to you'
                    : 'Opens LEGO.com'}
                  withArrow position="bottom" multiline w={220}
                  events={{ hover: true, focus: true, touch: false }}
                >
                  <Button
                    variant="default"
                    leftSection={<IconShoppingCart size={16} />}
                    rightSection={legoOnSale ? (
                      <Badge size="sm" variant="filled" color="red" radius="xl" px={8}>
                        -{Math.round(legoPrice.salePercentage)}%
                      </Badge>
                    ) : undefined}
                    component="a"
                    href={legoProductUrl(set.id, legoShop.locale)}
                    target="_blank"
                    rel={affiliateRel}
                    onClick={() => trackEvent('LEGO Shop Click', {
                      source: 'set-button', set: set.id, sale: legoOnSale ? 'yes' : 'no',
                    })}
                    radius="xl" size="sm"
                  >
                    <span className="button-text-full">
                      {legoPrice?.formatted ? `Buy at LEGO.com · ${legoPrice.formatted}` : 'Buy at LEGO.com'}
                    </span>
                    <span className="button-text-short">
                      {legoPrice?.formatted || 'Buy'}
                    </span>
                  </Button>
                </Tooltip>
              )}
              <Button
                variant="filled"
                leftSection={<IconChecklist size={16} />}
                rightSection={totalCheckProgress.checked > 0 ? (
                  <Badge
                    size="sm" variant="filled" radius="xl" px={8}
                    color={totalCheckProgress.percentage >= 100 ? 'green' : 'blue'}
                    style={{ backgroundColor: totalCheckProgress.percentage >= 100 ? undefined : 'rgba(255,255,255,0.25)' }}
                  >
                    {totalCheckProgress.percentage >= 100 ? 100 : Math.min(99, Math.round(totalCheckProgress.percentage))}%
                  </Badge>
                ) : isSortModeNew() ? (
                  <Badge size="sm" variant="filled" radius="xl" px={8} color="yellow" style={{ color: '#212529' }}>
                    New
                  </Badge>
                ) : undefined}
                onClick={() => {
                  trackEvent('Set Sort Opened', {
                    intent: totalCheckProgress.checked > 0 ? 'continue' : 'start',
                    build: activeBuild ? 'alternate' : 'complete',
                  });
                  router.push(`/set/${id}/sort`);
                }}
                radius="xl" size="sm"
                style={{ transition: 'all 0.25s ease' }}
              >
                <span className="button-text-full">{totalCheckProgress.checked > 0 ? 'Continue sorting' : 'Start sorting'}</span>
                <span className="button-text-short">Sort</span>
              </Button>
              {/* Downloads achter één menu: Sort is de enige primaire actie */}
              <Menu position="bottom-end" shadow="md" width={260} radius="md">
                <Menu.Target>
                  <Button
                    variant="light" color="gray"
                    leftSection={<IconDownload size={16} />}
                    rightSection={<IconChevronDown size={14} />}
                    loading={isGeneratingPdf}
                    radius="xl" size="sm"
                    style={{ transition: 'all 0.25s ease', color: '#495057' }}
                  >
                    Download
                  </Button>
                </Menu.Target>
                <Menu.Dropdown>
                  {pdfExists && (
                    <Menu.Item leftSection={<IconFileTypePdf size={18} />} onClick={handleDownloadPdf}>
                      <Text size="sm" fw={600}>PDF</Text>
                      <Text size="xs" c="dimmed">Printable overview of every bag</Text>
                    </Menu.Item>
                  )}
                  <Menu.Item
                    leftSection={<IconFileSpreadsheet size={18} />}
                    onClick={() => downloadSetBsx(id, set.bags.filter(b => !b.leftover))}
                  >
                    <Text size="sm" fw={600}>BSX</Text>
                    <Text size="xs" c="dimmed">BrickStore/BrickStock inventory, with bag numbers as remarks</Text>
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            </Group>
          </Group>

          {/* Search within set: free text + color filter, results show bag locations */}
          <Box
            mb="md"
            onFocusCapture={() => setPartSearchFocused(true)}
            onBlurCapture={(e) => {
              // Alleen sluiten als de focus de hele zoeker verlaat (niet bij
              // focus-verplaatsing van input naar een kleurchip)
              if (!e.currentTarget.contains(e.relatedTarget)) setPartSearchFocused(false);
            }}
          >
            <TextInput
              aria-label="Search parts in this set"
              placeholder="Search parts in this set by name, color or ID..."
              value={partSearch}
              onChange={(e) => setPartSearch(e.currentTarget.value)}
              leftSection={<IconSearch size={16} />}
              rightSection={partFilterActive ? (
                <ActionIcon variant="subtle" color="gray" radius="xl" size="sm" aria-label="Clear search" onClick={() => { setPartSearch(''); setSelectedColors([]); }}>
                  <IconX size={14} />
                </ActionIcon>
              ) : null}
              radius="xl"
              size="sm"
            />
            {colorOptions.length > 1 && (
              <Collapse in={partSearchFocused || partFilterActive}>
              <Group
                gap={6} mt={8}
                // Voorkom dat een chip-klik eerst de input blurt: anders klapt
                // de rij in vóór de klik landt (Safari geeft knoppen geen focus)
                onMouseDown={(e) => e.preventDefault()}
              >
                {colorOptions.map((c) => {
                  const selected = selectedColors.includes(c.name);
                  return (
                    <Box
                      key={c.name}
                      component="button"
                      type="button"
                      onClick={() => setSelectedColors(prev => selected ? prev.filter(n => n !== c.name) : [...prev, c.name])}
                      aria-pressed={selected}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        padding: '3px 10px', borderRadius: 999, cursor: 'pointer',
                        fontSize: 12.5, fontWeight: selected ? 700 : 500, fontFamily: 'inherit',
                        backgroundColor: selected ? '#e7f5ff' : '#f8f9fa',
                        color: selected ? '#1971c2' : '#495057',
                        border: selected ? '1px solid #74c0fc' : '1px solid #e9ecef',
                        transition: 'all 0.15s ease',
                      }}
                    >
                      {c.hex && (
                        <Box style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: `#${String(c.hex).replace(/^#/, '')}`, border: '1px solid rgba(0,0,0,0.15)', flexShrink: 0 }} />
                      )}
                      {c.name}
                    </Box>
                  );
                })}
              </Group>
              </Collapse>
            )}
          </Box>

          {/* Search results */}
          {partFilterActive && (
            <Paper withBorder radius="md" p="md" mb="md" style={{ backgroundColor: '#fff' }}>
              {searchResults.length === 0 ? (
                <Text size="sm" c="dimmed">No parts match your search.</Text>
              ) : (
                <>
                  <Text size="sm" fw={600} mb="sm">
                    {searchResults.length} part{searchResults.length !== 1 ? 's' : ''} · {searchResults.reduce((s, r) => s + r.total, 0)} pieces found
                  </Text>
                  <Stack gap={0}>
                    {searchResults.slice(0, 60).map((result) => {
                      const locations = partLocationsMap[result.key] || [];
                      return (
                        <Group key={result.key} gap="sm" wrap="nowrap" py={8} style={{ borderTop: '1px solid #f1f3f4' }}>
                          <Box style={{ width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <img
                              src={getThumbUrl(result.part.imageUrl, 96)}
                              alt={result.part.name || `Part ${result.part.partId}`}
                              style={{ maxWidth: 48, maxHeight: 48, mixBlendMode: 'multiply' }}
                              loading="lazy"
                            />
                          </Box>
                          <Box style={{ minWidth: 0, flex: 1 }}>
                            <Text size="sm" fw={600} lineClamp={1}>{result.part.name || `Part ${result.part.partId}`}</Text>
                            <Group gap={6} wrap="nowrap">
                              {result.part.colorHex && (
                                <Box style={{ width: 9, height: 9, borderRadius: '50%', backgroundColor: `#${String(result.part.colorHex).replace(/^#/, '')}`, border: '1px solid rgba(0,0,0,0.15)', flexShrink: 0 }} />
                              )}
                              <Text size="xs" c="dimmed" truncate>
                                {[result.part.colorName, result.part.partId].filter(Boolean).join(' · ')} · {result.total}x
                              </Text>
                            </Group>
                          </Box>
                          <Group gap={4} justify="flex-end" style={{ flexShrink: 0, maxWidth: '45%' }}>
                            {locations.map((loc) => (
                              <Badge
                                key={loc.bagNumber}
                                variant="light" radius="xl" size="md"
                                style={{ textTransform: 'none', cursor: 'pointer', backgroundColor: '#f1f3f5', color: '#495057' }}
                                onClick={() => jumpToBag(loc.bagNumber, result.key)}
                              >
                                {loc.chipLabel} · {loc.quantity}x
                              </Badge>
                            ))}
                          </Group>
                        </Group>
                      );
                    })}
                  </Stack>
                  {searchResults.length > 60 && (
                    <Text size="xs" c="dimmed" mt="xs">Showing the first 60 results. Refine your search to see the rest.</Text>
                  )}
                </>
              )}
            </Paper>
          )}

          {/* Admin Filters */}
          {adminMode && (
            <Group mb="md" gap="md">
              <Checkbox label="Show only bags with temp IDs" checked={showOnlyBagsWithTempIds} onChange={(event) => setShowOnlyBagsWithTempIds(event.currentTarget.checked)} />
              <Checkbox label="Show bag 0 (unassigned parts)" checked={showBag0} onChange={(event) => setShowBag0(event.currentTarget.checked)} />
              <Checkbox label="Show only decorated parts" checked={showOnlyDecoratedParts} onChange={(event) => setShowOnlyDecoratedParts(event.currentTarget.checked)} styles={{ label: { color: '#9c36b5', fontWeight: 500 } }} />
              <Checkbox label={`Show only missing images${Object.keys(failedImages).length > 0 ? ` (${Object.keys(failedImages).length})` : ''}`} checked={showOnlyMissingImages} onChange={(event) => setShowOnlyMissingImages(event.currentTarget.checked)} styles={{ label: { color: '#fa5252', fontWeight: 500 } }} />
              {Object.keys(multiItemParts).length > 0 && (
                <Checkbox label={`Show multi-item parts (${Object.keys(multiItemParts).length})`} checked={showOnlyMultiItemParts} onChange={(event) => setShowOnlyMultiItemParts(event.currentTarget.checked)} styles={{ label: { color: '#e67700', fontWeight: 500 } }} />
              )}
              <NumberInput
                label="Sticker sheets" value={set.sticker_sheets || 0}
                onChange={async (value) => {
                  try {
                    await api.updateStickerSheets(id, value);
                    const data = await api.fetchSetById(id, adminMode, null, catalogShop);
                    setSet(data);
                  } catch (error) { console.error('Error updating sticker sheets:', error); }
                }}
                min={0} max={10} size="xs" style={{ width: '120px' }}
              />
            </Group>
          )}

          {/* Multi-build switcher (3-in-1 / 2-in-1 sets) */}
          {set.builds && set.builds.length > 0 && (
            <Paper radius="md" p="sm" mb="md" style={{ border: '1px solid #e9ecef' }}>
              <Group gap="sm" align="center" wrap="wrap">
                <Text size="sm" fw={600} c="dimmed" style={{ flexShrink: 0 }}>What are you building?</Text>
                {(() => {
                  // Union-sets (bv. Venomized Groot) hebben geen hoofdbuild:
                  // build_count telt dan alleen de varianten
                  const hasMainBuild = (set.build_count || 0) > set.builds.length;
                  const mainLabel = hasMainBuild
                    ? `${set.main_build_name || set.name} (main build)`
                    : 'All bags';
                  const buildName = (b, i) => b.name || `Build ${i + (hasMainBuild ? 2 : 1)}`;
                  // Vanaf 5 opties past een rij tabs nergens meer (mobiel al
                  // helemaal niet) → combobox met stukkenaantal als hint
                  if (set.builds.length + 1 > 4) {
                    return (
                      <Select
                        size="sm"
                        value={activeBuild || 'complete'}
                        onChange={(v) => { if (v && v !== (activeBuild || 'complete')) switchBuild(v); }}
                        disabled={buildSwitching}
                        allowDeselect={false}
                        data={[
                          { value: 'complete', label: mainLabel },
                          ...set.builds.map((b, i) => ({
                            value: b.code,
                            label: `${buildName(b, i)} · ${(b.pieces || 0).toLocaleString('en-US')} pieces`,
                          })),
                        ]}
                        style={{ minWidth: 240, maxWidth: '100%' }}
                      />
                    );
                  }
                  return (
                    /* Scroll-container: de control kan niet wrappen en loopt op
                       mobiel anders buiten beeld */
                    <Box style={{ overflowX: 'auto', maxWidth: '100%', WebkitOverflowScrolling: 'touch' }}>
                    <SegmentedControl
                      size="sm"
                      value={activeBuild || 'complete'}
                      onChange={switchBuild}
                      disabled={buildSwitching}
                      data={[
                        {
                          value: 'complete',
                          label: (
                            <span style={{ display: 'inline-block', maxWidth: 'min(240px, 42vw)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'bottom' }}>
                              {mainLabel}
                            </span>
                          ),
                        },
                        ...set.builds.map((b, i) => ({
                          label: (
                            <span style={{ display: 'inline-block', maxWidth: 'min(220px, 42vw)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'bottom' }}>
                              {buildName(b, i)}
                            </span>
                          ),
                          value: b.code,
                        })),
                      ]}
                    />
                    </Box>
                  );
                })()}
                {adminMode && (
                  <Tooltip label={activeBuild ? 'Rename this build' : 'Rename main build (e.g. "Townhouse")'} withArrow>
                    <ActionIcon
                      variant="light" color="blue" radius="xl" size="md"
                      onClick={() => {
                        const current = activeBuild
                          ? (set.builds.find(b => b.code === activeBuild)?.name || '')
                          : (set.main_build_name || '');
                        setBuildNameInput(current);
                        setBuildNameModalOpen(true);
                      }}
                      aria-label="Rename build"
                    >
                      <IconEdit size={15} />
                    </ActionIcon>
                  </Tooltip>
                )}
                {buildSwitching && <Loader size="xs" />}
                {adminMode && (
                  <Tooltip label={`Pick an image for ${activeBuild || 'the main build'} from LEGO's product photos`} withArrow>
                    <ActionIcon
                      variant="light" color="grape" radius="xl" size="md"
                      onClick={async () => {
                        setBuildImagePickerOpen(true);
                        if (!buildImageCandidates) {
                          try {
                            const data = await api.getBuildImageCandidates(id);
                            setBuildImageCandidates(data.images || []);
                          } catch (err) {
                            setBuildImageCandidates([]);
                            notifyError(err, { title: 'Failed to load LEGO photos' });
                          }
                        }
                      }}
                      aria-label="Pick build image"
                    >
                      <IconPhoto size={15} />
                    </ActionIcon>
                  </Tooltip>
                )}
                {adminMode && activeBuild && (
                  <Tooltip label="Delete this build. The set itself stays untouched." withArrow>
                    <ActionIcon
                      variant="light" color="red" radius="xl" size="md"
                      onClick={() => setSetConfirmAction({ type: 'delete-build', buildCode: activeBuild })}
                      aria-label="Delete this build"
                    >
                      <IconTrash size={15} />
                    </ActionIcon>
                  </Tooltip>
                )}
              </Group>
              {set.build_style === 'shared_variants' && (
                <Text size="sm" mt={6}>
                  Build the shared base once, then choose one variant. The variants reuse parts from the same box.
                  {!activeBuild && ' All bags shows the physical inventory, including parts for all variants.'}
                </Text>
              )}
              {activeBuild && (
                <Text size="sm" c="dimmed" mt={6}>
                  This build uses {set.builds.find(b => b.code === activeBuild)?.pieces} of the set&apos;s pieces,
                  grouped in build order. Parts you don&apos;t need are listed under &ldquo;Left over&rdquo;.
                </Text>
              )}
              <Modal
                opened={buildImagePickerOpen}
                onClose={() => setBuildImagePickerOpen(false)}
                title={`Pick a LEGO photo for: ${activeBuild
                  ? (set.builds.find(b => b.code === activeBuild)?.name || activeBuild)
                  : `${set.main_build_name || set.name} (main build)`}`}
                size="xl"
              >
                <form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    const url = new FormData(e.currentTarget).get('customUrl')?.toString().trim();
                    if (!url) return;
                    try {
                      await api.setBuildImage(id, activeBuild || 'main', url);
                      buildCacheRef.current.clear();
                      const data = await api.fetchSetById(id, adminMode, activeBuild, catalogShop);
                      setSet(data);
                      setBuildImagePickerOpen(false);
                    } catch (err) { notifyError(err, { title: 'Failed to set image' }); }
                  }}
                >
                  <Group gap="xs" mb="md" wrap="nowrap">
                    <TextInput
                      name="customUrl" size="xs" style={{ flex: 1 }}
                      placeholder="Or paste an image URL from lego.com or brickset.com (right-click a photo, copy image address)"
                    />
                    <Button type="submit" size="xs" variant="light">Use URL</Button>
                  </Group>
                </form>
                {buildImageCandidates === null ? (
                  <Center p="xl"><Loader /></Center>
                ) : buildImageCandidates.length === 0 ? (
                  <Text c="dimmed" size="sm">No LEGO product photos found for this set.</Text>
                ) : (
                  <Box style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: 10 }}>
                    {buildImageCandidates.map((url) => (
                      <Box
                        key={url}
                        role="button" tabIndex={0}
                        onClick={async () => {
                          try {
                            await api.setBuildImage(id, activeBuild || 'main', url);
                            buildCacheRef.current.clear();
                            const data = await api.fetchSetById(id, adminMode, activeBuild, catalogShop);
                            setSet(data);
                            setBuildImagePickerOpen(false);
                          } catch (err) { notifyError(err, { title: 'Failed to set image' }); }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            e.currentTarget.click();
                          }
                        }}
                        style={{
                          border: '1px solid #e9ecef', borderRadius: 10, padding: 6,
                          cursor: 'pointer', backgroundColor: '#fff', textAlign: 'center',
                        }}
                      >
                        <img
                          src={`${url}${url.includes('?') ? '&' : '?'}width=320&height=240&rmode=max`}
                          alt="" loading="lazy"
                          style={{ maxWidth: '100%', height: 140, objectFit: 'contain' }}
                          onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
                        />
                      </Box>
                    ))}
                  </Box>
                )}
              </Modal>
            </Paper>
          )}

          {/* Quick Navigation for Bags */}
          {visibleBags.length > 3 && (() => {
            const filteredBags = visibleBags;
            const quickbarItems = [];
            filteredBags.forEach(bag => {
              if (bag.displayNumbers && bag.displayNumbers.length > 0) {
                bag.displayNumbers.forEach(num => {
                  if (!isNaN(parseInt(num))) {
                    quickbarItems.push({ displayNum: num, bagNumber: bag.bagNumber, bag: bag, isNumeric: true });
                  }
                });
              } else if (!isNaN(parseInt(bag.bagNumber))) {
                quickbarItems.push({ displayNum: bag.bagNumber, bagNumber: bag.bagNumber, bag: bag, isNumeric: true });
              }
            });
            quickbarItems.sort((a, b) => parseInt(a.displayNum) - parseInt(b.displayNum));
            const nonNumericBags = filteredBags.filter(b => isNaN(parseInt(b.bagNumber)) && (!b.displayNumbers || b.displayNumbers.length === 0));
            const totalNumericItems = quickbarItems.length;
            const isLargeSet = totalNumericItems > 20;
            const rangeSize = 10;
            const maxDisplayNumber = quickbarItems.length > 0 ? Math.max(...quickbarItems.map(item => parseInt(item.displayNum))) : 0;
            const numRanges = Math.ceil(maxDisplayNumber / rangeSize);
            const ranges = Array.from({ length: numRanges }, (_, i) => ({ start: i * rangeSize + 1, end: (i + 1) * rangeSize }));
            const currentRange = selectedBagRange < numRanges ? selectedBagRange : 0;
            const itemsInRange = isLargeSet
              ? quickbarItems.filter(item => {
                  const num = parseInt(item.displayNum);
                  return num >= ranges[currentRange]?.start && num <= ranges[currentRange]?.end;
                })
              : quickbarItems;

            const scrollToBag = (bagValue, isOpen) => {
              if (isMobile) setQuickJumpOpen(false);
              const element = document.querySelector(`[data-bag-id="${bagValue}"]`);
              if (!element) return;
              const doScroll = (smooth = true) => {
                const quickJumpHeight = quickJumpRef.current?.offsetHeight || 0;
                const headerHeight = 70;
                const extraSpacing = 10;
                const totalOffset = headerHeight + quickJumpHeight + extraSpacing;
                const elementRect = element.getBoundingClientRect();
                const absoluteTop = elementRect.top + window.pageYOffset;
                const scrollTarget = absoluteTop - totalOffset;
                window.scrollTo({ top: scrollTarget, behavior: smooth ? 'smooth' : 'instant' });
              };
              if (!isOpen) {
                setOpenItems([...openItems, bagValue]);
                setTimeout(() => doScroll(false), 50);
                setTimeout(() => doScroll(true), 400);
              } else {
                doScroll(true);
              }
            };

            return (
              <Box
                ref={quickJumpRef}
                style={{
                  position: '-webkit-sticky', position: 'sticky', top: '70px', zIndex: 100,
                  backgroundColor: '#ffffff', borderRadius: '12px', padding: '12px 16px', marginBottom: '16px',
                  boxShadow: isQuickJumpSticky ? '0 4px 12px rgba(0, 0, 0, 0.12)' : '0 2px 8px rgba(0, 0, 0, 0.04)',
                  border: '1px solid #e9ecef', transition: 'box-shadow 0.15s ease',
                }}
              >
                {isMobile && (() => {
                  const activeIdx = activeBagInView ? quickbarItems.findIndex(it => `bag-${it.bagNumber}` === activeBagInView) : -1;
                  const activeItem = activeIdx >= 0 ? quickbarItems[activeIdx] : null;
                  const prevItem = activeIdx > 0 ? quickbarItems[activeIdx - 1] : null;
                  const nextItem = activeIdx >= 0 && activeIdx < quickbarItems.length - 1
                    ? quickbarItems[activeIdx + 1]
                    : (activeIdx === -1 ? quickbarItems[0] : null);
                  const noun = allBagsVirtual ? 'Group' : 'Bag';
                  return (
                    <Group justify="space-between" wrap="nowrap" gap="xs">
                      <Group
                        gap={6} wrap="nowrap"
                        style={{ minWidth: 0, flex: 1, cursor: 'pointer' }}
                        onClick={() => setQuickJumpOpen(o => !o)}
                        role="button" tabIndex={0} aria-expanded={quickJumpOpen}
                        aria-label={quickJumpOpen ? 'Collapse bag navigation' : 'Expand bag navigation'}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setQuickJumpOpen(o => !o); } }}
                      >
                        <IconChevronDown size={16} style={{ transform: quickJumpOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s ease', color: '#868e96', flexShrink: 0 }} />
                        <Text size="sm" fw={600} truncate>
                          {activeItem ? `${noun} ${activeItem.displayNum} of ${maxDisplayNumber}` : `Jump to ${noun.toLowerCase()}`}
                        </Text>
                      </Group>
                      <Group gap={4} wrap="nowrap">
                        <ActionIcon
                          variant="light" color="gray" radius="xl" size="md"
                          disabled={!prevItem} aria-label={`Previous ${noun.toLowerCase()}`}
                          onClick={() => prevItem && scrollToBag(`bag-${prevItem.bagNumber}`, openItemsSet.has(`bag-${prevItem.bagNumber}`))}
                        >
                          <IconArrowLeft size={15} />
                        </ActionIcon>
                        <ActionIcon
                          variant="light" color="gray" radius="xl" size="md"
                          disabled={!nextItem} aria-label={`Next ${noun.toLowerCase()}`}
                          onClick={() => nextItem && scrollToBag(`bag-${nextItem.bagNumber}`, openItemsSet.has(`bag-${nextItem.bagNumber}`))}
                        >
                          <IconArrowRight size={15} />
                        </ActionIcon>
                      </Group>
                    </Group>
                  );
                })()}

                {(!isMobile || quickJumpOpen) && (
                <Box mt={isMobile ? 10 : 0}>
                {isLargeSet && ranges.length > 1 && (
                  <Group gap="xs" mb="sm" style={{ flexWrap: 'wrap' }}>
                    <Text size="xs" fw={500} c="dimmed" style={{ marginRight: '4px' }}>Jump to:</Text>
                    {ranges.map((range, index) => (
                      <Button
                        key={index} size="xs" variant={currentRange === index ? 'filled' : 'light'} radius="md"
                        onClick={() => setSelectedBagRange(index)}
                        style={{
                          minWidth: '60px', height: '26px', padding: '0 10px',
                          fontWeight: currentRange === index ? 600 : 500,
                          backgroundColor: currentRange === index ? '#228be6' : '#f1f3f5',
                          color: currentRange === index ? '#fff' : '#495057',
                          border: 'none', transition: 'all 0.15s ease',
                        }}
                      >
                        {range.start}-{range.end}
                      </Button>
                    ))}
                  </Group>
                )}

                <Box data-quick-jump-content style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px' }}>
                  {itemsInRange.map((item) => {
                    const bagValue = `bag-${item.bagNumber}`;
                    const isOpen = openItemsSet.has(bagValue);
                    const isActive = activeBagInView === bagValue;
                    const baseBg = '#f8f9fa';
                    const baseBorder = '1px solid #e9ecef';
                    return (
                      <Box
                        key={item.displayNum} className="quick-jump-box"
                        component="button" type="button"
                        aria-label={`Jump to ${item.isNumeric ? 'bag' : 'group'} ${item.displayNum}`}
                        onClick={() => scrollToBag(bagValue, isOpen)}
                        style={{
                          width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                          backgroundColor: isActive ? '#228be6' : baseBg,
                          color: isActive ? '#fff' : '#495057',
                          borderRadius: '8px', fontSize: '13px', fontWeight: isActive ? 700 : 500,
                          cursor: 'pointer',
                          border: isActive ? '2px solid #228be6' : baseBorder,
                          boxShadow: isActive ? '0 2px 8px rgba(34, 139, 230, 0.4)' : 'none',
                          transition: 'all 0.15s ease', padding: 0, fontFamily: 'inherit',
                        }}
                        onMouseEnter={(e) => { if (!isActive) { e.currentTarget.style.backgroundColor = '#e9ecef'; e.currentTarget.style.borderColor = '#dee2e6'; } }}
                        onMouseLeave={(e) => { if (!isActive) { e.currentTarget.style.backgroundColor = baseBg; e.currentTarget.style.borderColor = '#e9ecef'; } }}
                      >
                        {item.displayNum}
                      </Box>
                    );
                  })}

                  {nonNumericBags.map((bag) => {
                    const bagValue = `bag-${bag.bagNumber}`;
                    const isOpen = openItemsSet.has(bagValue);
                    const isActive = activeBagInView === bagValue;
                    return (
                      <Box
                        key={bag.bagNumber}
                        component="button" type="button"
                        aria-label={`Jump to ${bag.leftover ? 'left over group' : `group ${bag.bagNumber}`}`}
                        onClick={() => scrollToBag(bagValue, isOpen)}
                        style={{
                          height: '32px', padding: '0 12px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                          backgroundColor: isActive ? '#228be6' : '#f8f9fa',
                          color: isActive ? '#fff' : '#495057',
                          borderRadius: '8px', fontSize: '12px', fontWeight: isActive ? 600 : 500,
                          cursor: 'pointer',
                          border: isActive ? '2px solid #228be6' : '1px solid #e9ecef',
                          boxShadow: isActive ? '0 2px 8px rgba(34, 139, 230, 0.4)' : 'none',
                          transition: 'all 0.15s ease', marginLeft: '8px', fontFamily: 'inherit',
                        }}
                        onMouseEnter={(e) => { if (!isActive) { e.currentTarget.style.backgroundColor = '#e9ecef'; } }}
                        onMouseLeave={(e) => { if (!isActive) { e.currentTarget.style.backgroundColor = '#f8f9fa'; } }}
                      >
                        {bag.bagNumber === 'Extra parts' ? 'Extra' : bag.bagNumber}
                      </Box>
                    );
                  })}
                </Box>
                </Box>
                )}
              </Box>
            );
          })()}

          {set.bags && set.bags.length > 0 && (
            <Group justify="flex-end" gap="xs" mb="sm" wrap="nowrap">
              <Text size="sm" c="dimmed" id="part-order-label">Order parts by</Text>
              <SegmentedControl
                size="xs" radius="xl"
                aria-labelledby="part-order-label"
                value={partOrder}
                onChange={changePartOrder}
                data={[
                  { value: 'color', label: 'Color' },
                  { value: 'type', label: 'Part type' },
                ]}
              />
            </Group>
          )}

          {/* Bags Accordion */}
          {set.bags && set.bags.length > 0 ? (
            <Accordion
              variant="separated" multiple value={openItems} onChange={setOpenItems}
              classNames={{ item: 'bag-accordion-item', control: 'bag-accordion-control' }}
              styles={{
                panel: { backgroundColor: '#f8f9fa' },
              }}
            >
              {(() => {
                // In-feed ad every 5 bags, capped at 3 per page. Never during
                // check mode (the core utility stays clean), never for admins,
                // never trailing after the last bag.
                const showAds = !adminMode;
                return visibleBags.flatMap((bag, i) => {
                  const currentItemValue = `bag-${bag.bagNumber}`;
                  const isItemOpen = openItemsSet.has(currentItemValue);
                  const items = [
                    <BagAccordionItem
                      key={bag.bagNumber} bag={bag} isOpen={isItemOpen} adminMode={adminMode}
                      onDisconnectPart={handleDisconnectPart} onDeletePart={handleDeletePart}
                      onEditPart={handleUpdatePartId} onQuantityEditPart={handleUpdateQuantity}
                      onMovePart={handleMovePart}
                      onEditDisplayNumbers={handleEditDisplayNumbers}
                      onUploadPreviewImage={handleUploadPreviewImage} onDeletePreviewImage={handleDeletePreviewImage}
                      extraParts={extraParts} setId={id} svgStatus={svgStatus}
                      showOnlyDecoratedParts={showOnlyDecoratedParts} showOnlyMissingImages={showOnlyMissingImages}
                      showOnlyMultiItemParts={showOnlyMultiItemParts} multiItemParts={multiItemParts}
                      failedImages={failedImages} onImageError={handleImageError}
                      shouldLoad={adminMode ? shouldBagLoad(bag.bagNumber) : isItemOpen} isLoaded={isBagLoaded(bag.bagNumber)}
                      onBagImageLoad={handleBagImageLoad}
                      onAddPart={handleAddPart} onDeleteBag={handleDeleteBag}
                      partLocations={partLocationsMap} onJumpToBag={jumpToBag}
                      highlightedPartKeys={highlightedPartKeys}
                      partOrder={partOrder}
                    />,
                  ];
                  const adNumber = (i + 1) / 5;
                  if (showAds && Number.isInteger(adNumber) && adNumber <= 3 && i + 1 < visibleBags.length) {
                    items.push(<AdSlot key={`ad-${adNumber}`} />);
                  }
                  return items;
                });
              })()}
            </Accordion>
          ) : (
            <Text c="dimmed" ta="center" py="xl">No bags found for this set.</Text>
          )}
        </Box>
      </Paper>

      {!adminMode && <WhatsNext set={set} sortProgress={totalCheckProgress} />}

      {/* Move part to another bag */}
      {adminMode && moveModal && (
        <Modal
          opened
          onClose={() => setMoveModal(null)}
          title={`Move part ${moveModal.part.partId} to another bag`}
          size="sm"
        >
          <Stack gap="md">
            <Group gap="sm" wrap="nowrap">
              {moveModal.part.image_url && (
                <Image src={getImageUrl(moveModal.part.image_url)} w={56} h={56}
                  fit="contain" alt={moveModal.part.partId} />
              )}
              <Box>
                <Text size="sm" fw={600}>{moveModal.part.name || moveModal.part.partId}</Text>
                <Text size="xs" c="dimmed">
                  {moveModal.part.color_name || 'No color'} · {moveModal.part.quantity} available
                </Text>
              </Box>
            </Group>
            <Select
              label="Destination bag"
              placeholder="Choose a bag"
              value={moveModal.toBagId ? String(moveModal.toBagId) : null}
              onChange={(v) => setMoveModal(m => ({ ...m, toBagId: v }))}
              data={(set?.bags || [])
                .filter(b => b.id !== moveModal.part.bagId)
                .map(b => ({
                  value: String(b.id),
                  label: isNaN(parseInt(b.bagNumber)) ? b.bagNumber : `Bag ${b.bagNumber}`,
                }))}
              searchable
            />
            <NumberInput
              label="Quantity" value={moveModal.quantity}
              onChange={(v) => setMoveModal(m => ({ ...m, quantity: Number(v) || 1 }))}
              min={1} max={moveModal.part.quantity} allowDecimal={false}
              description={moveModal.quantity >= moveModal.part.quantity
                ? 'The entire part quantity will be moved'
                : `${moveModal.part.quantity - moveModal.quantity} will remain in this bag`}
            />
            <Group justify="flex-end" gap="sm">
              <Button variant="light" onClick={() => setMoveModal(null)}>Cancel</Button>
              <Button
                color="grape" loading={moveSaving} disabled={!moveModal.toBagId}
                leftSection={<IconArrowsExchange size={16} />}
                onClick={async () => {
                  setMoveSaving(true);
                  try {
                    await api.movePartToBag(id, moveModal.part.bagId, moveModal.part.partId,
                      moveModal.part.color_id ?? null, parseInt(moveModal.toBagId),
                      moveModal.quantity);
                    const data = await api.fetchSetById(id, adminMode, activeBuild, catalogShop);
                    setSet(data);
                    await updateExtraParts();
                    setMoveModal(null);
                  } catch (err) {
                    notifyError(err, { title: 'Failed to move part' });
                  } finally {
                    setMoveSaving(false);
                  }
                }}
              >
                Move part
              </Button>
            </Group>
          </Stack>
        </Modal>
      )}

      {/* Display Numbers Edit Modal */}
      {adminMode && (
        <Modal
          opened={displayNumbersModalOpen}
          onClose={() => setDisplayNumbersModalOpen(false)}
          title={`Edit Display Numbers - Bag ${editingBag?.bagNumber}`}
          size="sm"
        >
          <Stack gap="md">
            <Text size="sm" c="dimmed">
              Enter bag numbers to display, separated by commas. For example: &quot;20, 21&quot; will show both bag 20 and 21 images but link to this single bag.
            </Text>
            <TextInput label="Display Numbers" placeholder="e.g. 20, 21" value={displayNumbersInput} onChange={(e) => setDisplayNumbersInput(e.target.value)} />
            <Group justify="flex-end" gap="sm">
              <Button variant="light" onClick={() => setDisplayNumbersModalOpen(false)}>Cancel</Button>
              <Button onClick={handleSaveDisplayNumbers}>Save</Button>
            </Group>
          </Stack>
        </Modal>
      )}

      {adminMode && (
        <Modal
          opened={buildNameModalOpen}
          onClose={() => setBuildNameModalOpen(false)}
          title={activeBuild ? 'Rename build' : 'Rename main build'}
          centered
          size="sm"
        >
          <form
            onSubmit={async (event) => {
              event.preventDefault();
              try {
                await api.updateBuildName(id, activeBuild || 'main', buildNameInput.trim());
                buildCacheRef.current.clear();
                const data = await api.fetchSetById(id, adminMode, activeBuild, catalogShop);
                setSet(data);
                setBuildNameModalOpen(false);
              } catch (error) {
                notifyError(error, { title: 'Failed to rename build' });
              }
            }}
          >
            <Stack gap="md">
              <TextInput
                label="Build name"
                description={activeBuild
                  ? 'Leave this empty to use the default build name.'
                  : 'Leave this empty to use the set name.'}
                value={buildNameInput}
                onChange={(event) => setBuildNameInput(event.currentTarget.value)}
                autoFocus
              />
              <Group justify="flex-end">
                <Button variant="default" onClick={() => setBuildNameModalOpen(false)}>Cancel</Button>
                <Button type="submit">Save name</Button>
              </Group>
            </Stack>
          </form>
        </Modal>
      )}

      {adminMode && (
        <Modal
          opened={setConfirmAction !== null}
          onClose={() => setSetConfirmAction(null)}
          title={setConfirmAction?.type === 'delete-bag'
            ? `Delete bag ${setConfirmAction.bagNumber}?`
            : setConfirmAction?.type === 'delete-build'
              ? `Delete build ${setConfirmAction.buildCode}?`
              : 'Delete preview image?'}
          centered
          size="sm"
        >
          <Text size="sm" c="dimmed" mb="lg">
            {setConfirmAction?.type === 'delete-bag'
              ? 'The bag and every part assigned to it will be permanently deleted. This cannot be undone.'
              : setConfirmAction?.type === 'delete-build'
                ? 'The build and all of its groups will be permanently deleted. The set itself will stay untouched.'
                : 'The preview image will be permanently deleted.'}
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setSetConfirmAction(null)}>Cancel</Button>
            <Button
              color="red"
              onClick={async () => {
                const action = setConfirmAction;
                setSetConfirmAction(null);
                if (!action) return;
                if (action.type === 'delete-bag') {
                  await confirmDeleteBag(action.bagId);
                  return;
                }
                if (action.type === 'delete-preview') {
                  await confirmDeletePreviewImage(action.bagId);
                  return;
                }
                try {
                  await api.deleteBuild(id, action.buildCode);
                  buildCacheRef.current.clear();
                  const data = await api.fetchSetById(id, adminMode, null, catalogShop);
                  setSet(data);
                  const params = new URLSearchParams(searchParams.toString());
                  params.delete('build');
                  const query = params.toString();
                  router.replace(`/set/${id}${query ? `?${query}` : ''}`, { scroll: false });
                } catch (error) {
                  notifyError(error, { title: 'Failed to delete build' });
                }
              }}
            >
              {setConfirmAction?.type === 'delete-bag'
                ? 'Delete bag'
                : setConfirmAction?.type === 'delete-build'
                  ? 'Delete build'
                  : 'Delete image'}
            </Button>
          </Group>
        </Modal>
      )}
    </Container>
  );
}

export default function SetPageClient({ initialShop, initialSet }) {
  return (
    <Suspense>
      <SetDetailPageContent initialSet={initialSet} initialShop={initialShop} />
    </Suspense>
  );
}
